import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { handleTokenAndPostRequest } from "../utils/utils";
import { Tooltip } from "@mui/material";
import {
  FaPaperPlane, FaTimes, FaSyncAlt, FaInbox, FaChevronDown, FaChevronUp,
  FaRobot, FaExclamationTriangle, FaCheckCircle, FaCog, FaArrowLeft,
  FaEnvelope, FaReply, FaPaperPlane as FaSend, FaUserCircle, FaUser,
  FaCommentDots, FaPhone, FaStickyNote, FaMagic, FaPlus, FaHome, FaChartLine,
  FaUpload,
} from "react-icons/fa";
import AIAgentSettingsModal from "../components/AIAgentSettingsModal";
import BuyExtraUsageModal from "../components/BuyExtraUsageModal";
import ContactSearchesModal from "../components/ContactSearchesModal";
import ContactActivityModal from "../components/ContactActivityModal";
import {
  AIHandoverNotesPopover,
  HandoverNotesButton,
  useHandoverNotesPopover,
  getContactIdentity,
} from "../components/AIHandoverNotesPopover";
import LeadScoreBadge from "../components/LeadScoreBadge";
import { smsCost, SMS_PRICE_PER_PART } from "../utils/sms";
import ContactTagPicker, { ContactTagChip } from "../components/ContactTagPicker";
import { DEMO_REVIEW, DEMO_INBOX } from "../demo/demoConversations";
import { getDemoThread } from "../demo/demoContactDetails";
import DemoBanner from "../demo/DemoBanner";
import useDemoNetworkGuard from "../demo/useDemoNetworkGuard";
import useAgentHasNoContacts from "../demo/useAgentHasContacts";
import UploadContactsModal from "../components/UploadContactsModal";

// Mirrors the breakpoint MainNav uses (768px) so the page layout flips at
// the same point the sidebar collapses into the hamburger top bar.
function useIsMobile() {
  const [m, setM] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mm = window.matchMedia("(max-width: 767px)");
    const l = (e) => setM(e.matches);
    mm.addEventListener("change", l);
    return () => mm.removeEventListener("change", l);
  }, []);
  return m;
}

// Convert a contact_key (lowercased email or trimmed phone) into the
// {contact_email, contact_mobile} shape /agent-set-contact-ai-ownership wants.
// We prefer the explicit contact_email when the row has one because it
// preserves the original case for the audit log.
function contactIdentityFromKey(contactKey, contactEmail) {
  const k = (contactKey || "").trim();
  if (contactEmail) {
    return { contact_email: contactEmail.trim().toLowerCase(), contact_mobile: null };
  }
  if (!k) return { contact_email: null, contact_mobile: null };
  if (k.includes("@")) return { contact_email: k.toLowerCase(), contact_mobile: null };
  return { contact_email: null, contact_mobile: k };
}

// AI/Me ownership pill toggle. Mirrors the pattern in Contacts.js so the
// behaviour is consistent across the platform.
function AIOwnerToggle({ aiOwns, onChange, disabled = false, size = "md", stopPropagation = true }) {
  const pad = size === "sm" ? "3px 8px" : "4px 10px";
  const fs  = size === "sm" ? 11 : 12;
  const ic  = size === "sm" ? 9  : 11;
  const click = (next) => (e) => {
    if (stopPropagation) e.stopPropagation();
    if (disabled || next === aiOwns) return;
    onChange(next);
  };
  return (
    <div
      role="radiogroup"
      aria-label="Who owns this relationship"
      onClick={(e) => { if (stopPropagation) e.stopPropagation(); }}
      style={{
        display: "inline-flex", borderRadius: 999,
        border: "1px solid #e5e7eb", background: "#f9fafb",
        padding: 2, gap: 2,
      }}
    >
      <button
        role="radio"
        aria-checked={aiOwns}
        disabled={disabled}
        onClick={click(true)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: pad, borderRadius: 999, border: "none",
          background: aiOwns ? "#0d9488" : "transparent",
          color: aiOwns ? "#fff" : "#6b7280",
          fontSize: fs, fontWeight: 600,
          cursor: disabled ? "not-allowed" : "pointer",
          // Only transition background — fading colour at the same time
          // briefly leaves white text over a half-transparent dark pill
          // (the light parent showing through), reading as a white-on-white
          // flash. Keep the colour swap instant.
          transition: "background 0.12s ease",
        }}
      >
        <FaRobot style={{ fontSize: ic }} /> AI
      </button>
      <button
        role="radio"
        aria-checked={!aiOwns}
        disabled={disabled}
        onClick={click(false)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: pad, borderRadius: 999, border: "none",
          background: !aiOwns ? "#1f2937" : "transparent",
          color: !aiOwns ? "#fff" : "#6b7280",
          fontSize: fs, fontWeight: 600,
          cursor: disabled ? "not-allowed" : "pointer",
          // See AI button above — background-only transition prevents the
          // white-on-white flash during deselection.
          transition: "background 0.12s ease",
        }}
      >
        <FaUser style={{ fontSize: ic - 1 }} /> Me
      </button>
    </div>
  );
}

// Copy shown after every AI/Me ownership toggle. Kept in one place so the
// wording stays consistent everywhere the toggle appears.
function ownershipToastMessage(aiOwns) {
  return aiOwns
    ? "AI now owns this relationship — it'll handle replies and outbound follow-ups."
    : "You own this relationship — the AI won't send outbound follow-ups.";
}

// Optimistic AI/Me ownership update with rollback on failure. The success
// toast fires immediately so the UI feels responsive — only an error toast
// is surfaced from the awaited request.
async function postOwnership({ contactKey, contactEmail, next, applyLocal, current, setToast }) {
  applyLocal(next);
  setToast?.({ severity: "info", message: ownershipToastMessage(next) });
  try {
    const identity = contactIdentityFromKey(contactKey, contactEmail);
    if (!identity.contact_email && !identity.contact_mobile) {
      throw new Error("No contact identity available");
    }
    await handleTokenAndPostRequest(
      `${window.api_host}/agent-set-contact-ai-ownership`,
      { contacts: [identity], ai_owns_relationship: next },
      { "Content-Type": "application/json" }
    );
  } catch (e) {
    applyLocal(current);
    setToast?.({ severity: "error", message: e?.response?.data?.message || "Failed to update ownership" });
  }
}

// ---------------------------------------------------------------------------
// Streaming follow-up compose
// ---------------------------------------------------------------------------
// The backend /ai-conversations/generate-followup endpoint streams the
// composing LLM's JSON output token-by-token over SSE when called with
// ?stream=1. These helpers parse that stream and surface incremental
// values of the JSON fields the agent UI actually edits — subject, body,
// sms_body, talking_points, preferred_channel — so the textareas fill in
// live as the model writes them.

const safeLocalGetForStream = (k) => {
  try { return localStorage.getItem(k); } catch (e) { return null; }
};

// Read a Fetch Response whose body is a text/event-stream and invoke
// onEvent({ event, data }) for each `event:` / `data:` block.
async function readSSEStream(response, onEvent) {
  if (!response.ok || !response.body) {
    let msg = `Stream request failed: ${response.status}`;
    try { const j = await response.json(); if (j?.message) msg = j.message; } catch (_) {}
    throw new Error(msg);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const raw = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      let evName = "message";
      const dataLines = [];
      raw.split("\n").forEach((line) => {
        if (line.startsWith("event:")) evName = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
      });
      if (dataLines.length) {
        let parsed = null;
        try { parsed = JSON.parse(dataLines.join("\n")); } catch (_) {}
        // The backend currently emits all events under the default
        // "message" name with a {type:...} discriminator in the JSON body,
        // so prefer parsed.type when present.
        const finalName = (parsed && parsed.type) ? parsed.type : evName;
        onEvent({ event: finalName, data: parsed });
      }
    }
  }
}

// Extract the (possibly partial) string value of a JSON field from a
// streaming buffer. Returns null until we've seen `"<key>":"`; returns the
// partial value (with JSON escapes decoded) once the value has started.
// Handles \n \t \r \\ \" \/ \uXXXX escapes safely while the value is still
// being written.
function extractStreamingJsonField(buf, key) {
  const marker = `"${key}":`;
  const i = buf.indexOf(marker);
  if (i < 0) return null;
  let j = i + marker.length;
  // skip whitespace between : and "
  while (j < buf.length && (buf[j] === " " || buf[j] === "\n" || buf[j] === "\r" || buf[j] === "\t")) j++;
  if (j >= buf.length || buf[j] !== '"') return null;
  j++; // past opening "
  let out = "";
  while (j < buf.length) {
    const ch = buf[j];
    if (ch === "\\") {
      const next = buf[j + 1];
      if (next === undefined) return out; // partial escape — stop here
      if (next === "n") { out += "\n"; j += 2; continue; }
      if (next === "t") { out += "\t"; j += 2; continue; }
      if (next === "r") { out += "\r"; j += 2; continue; }
      if (next === '"') { out += '"'; j += 2; continue; }
      if (next === "\\") { out += "\\"; j += 2; continue; }
      if (next === "/") { out += "/"; j += 2; continue; }
      if (next === "u") {
        if (j + 6 > buf.length) return out;
        out += String.fromCharCode(parseInt(buf.slice(j + 2, j + 6), 16));
        j += 6; continue;
      }
      out += next; j += 2; continue;
    }
    if (ch === '"') return out; // value fully written
    out += ch;
    j++;
  }
  return out; // still streaming
}

// Drive the streaming compose endpoint and surface incremental field
// updates through onPatch({ subject, body, sms_body, talking_points,
// channel }). onDone receives the final hydrated draft (or null) once
// persistence has run; onError receives an Error on failure.
async function streamGenerateFollowup({
  contactKey, extraInstructions, onPatch, onDone, onError,
}) {
  try {
    // Demo mode: the raw streaming fetch bypasses handleTokenAndPostRequest's
    // guard, so short-circuit here too. Resolve as a no-op so the caller's
    // Regenerate flow (which is also UI-gated on the card) never hits the API.
    if (typeof window !== 'undefined' && window.__demoMode) {
      onDone?.(null);
      return;
    }
    const jwt = safeLocalGetForStream("jwt_token");
    const urlToken = new URLSearchParams(window.location.search).get("token");
    const paramToken = urlToken || safeLocalGetForStream("param_token");
    const res = await fetch(
      `${window.api_host}/ai-conversations/generate-followup?stream=1`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          contact_key: contactKey,
          extra_instructions: (extraInstructions || "").trim() || undefined,
          stream: true,
          param_token: paramToken,
          source: window.location.href,
        }),
      },
    );

    let buf = "";
    const last = { subject: "", body: "", sms_body: "", talking_points: "", channel: "" };
    let doneDraft = null;
    let streamError = null;

    await readSSEStream(res, ({ event, data }) => {
      if (event === "delta" && data && typeof data.content === "string") {
        buf += data.content;
        const subject = extractStreamingJsonField(buf, "subject") ?? "";
        const body    = extractStreamingJsonField(buf, "body") ?? "";
        const sms     = extractStreamingJsonField(buf, "sms_body") ?? "";
        const talking = extractStreamingJsonField(buf, "talking_points") ?? "";
        const channel = extractStreamingJsonField(buf, "preferred_channel");

        const patch = {};
        if (subject !== last.subject) {
          patch.subject = subject;
          patch.email_subject = subject;
          last.subject = subject;
        }
        if (body !== last.body) {
          patch.body = body;
          patch.email_body = body;
          last.body = body;
        }
        if (sms !== last.sms_body) {
          patch.sms_body = sms;
          last.sms_body = sms;
        }
        if (talking !== last.talking_points) {
          patch.talking_points = talking;
          patch.call_note = talking;
          last.talking_points = talking;
        }
        if (channel && channel !== last.channel) {
          patch.channel = channel;
          last.channel = channel;
        }
        if (Object.keys(patch).length) onPatch?.(patch);
      } else if (event === "done") {
        doneDraft = (data && data.draft) || null;
      } else if (event === "error") {
        streamError = new Error((data && data.message) || "Failed to generate follow-up.");
      }
    });

    if (streamError) throw streamError;
    onDone?.(doneDraft);
  } catch (e) {
    onError?.(e);
  }
}

const fmtRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  const day = Math.round(h / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString();
};

export function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  const sev = toast.severity || "success";
  const bg = sev === "error" ? "#dc2626" : sev === "info" ? "#0d9488" : "#16a34a";
  const Icon = sev === "error" ? FaExclamationTriangle : sev === "info" ? FaRobot : FaCheckCircle;
  return (
    <div style={{
      position: "fixed", right: 20, bottom: 20, zIndex: 1000,
      background: bg, color: "#fff",
      padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600,
      boxShadow: "0 6px 16px rgba(0,0,0,0.18)", display: "flex", alignItems: "center", gap: 8,
      maxWidth: 420,
    }}>
      <Icon style={{ flexShrink: 0 }} /> <span>{toast.message}</span>
    </div>
  );
}

const SKELETON_KEYFRAMES = (
  <style>{`
    @keyframes skeletonPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
    @keyframes ai-followup-spin { to { transform: rotate(360deg); } }
  `}</style>
);

function DraftCardSkeleton() {
  return (
    <div style={{
      padding: 16, background: "#fff", border: "1px solid #e5e7eb",
      borderRadius: 12, marginBottom: 12,
      animation: "skeletonPulse 1.5s ease-in-out infinite",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ width: 70, height: 18, background: "#e5e7eb", borderRadius: 999 }} />
        <div style={{ width: 180, height: 14, background: "#e5e7eb", borderRadius: 6 }} />
        <div style={{ marginLeft: "auto", width: 80, height: 10, background: "#f3f4f6", borderRadius: 4 }} />
      </div>
      <div style={{ width: 220, height: 10, background: "#f3f4f6", borderRadius: 4, marginBottom: 14 }} />
      <div style={{ width: 60, height: 10, background: "#f3f4f6", borderRadius: 4, marginBottom: 6 }} />
      <div style={{ width: "100%", height: 36, background: "#f3f4f6", borderRadius: 8, marginBottom: 12 }} />
      <div style={{ width: 40, height: 10, background: "#f3f4f6", borderRadius: 4, marginBottom: 6 }} />
      <div style={{ width: "100%", height: 120, background: "#f3f4f6", borderRadius: 8, marginBottom: 12 }} />
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ width: 90, height: 32, background: "#e5e7eb", borderRadius: 8 }} />
        <div style={{ width: 100, height: 32, background: "#f3f4f6", borderRadius: 8 }} />
      </div>
    </div>
  );
}

function ConversationRowSkeleton() {
  return (
    <div style={{
      width: "100%", padding: "12px 14px",
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
      marginBottom: 8, display: "flex", gap: 12, alignItems: "center",
      animation: "skeletonPulse 1.5s ease-in-out infinite",
    }}>
      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#e5e7eb", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <div style={{ width: 160, height: 14, background: "#e5e7eb", borderRadius: 6 }} />
        </div>
        <div style={{ width: "70%", height: 10, background: "#f3f4f6", borderRadius: 4 }} />
      </div>
      <div style={{ width: 50, height: 10, background: "#f3f4f6", borderRadius: 4, flexShrink: 0 }} />
    </div>
  );
}

function ThreadMessageSkeleton({ outbound }) {
  return (
    <div style={{ display: "flex", justifyContent: outbound ? "flex-end" : "flex-start", marginBottom: 10 }}>
      <div style={{
        width: "60%",
        padding: "10px 14px", borderRadius: 12,
        background: outbound ? "#eef2ff" : "#f3f4f6",
        border: `1px solid ${outbound ? "#c7d2fe" : "#e5e7eb"}`,
        animation: "skeletonPulse 1.5s ease-in-out infinite",
      }}>
        <div style={{ width: 140, height: 10, background: "rgba(0,0,0,0.08)", borderRadius: 4, marginBottom: 8 }} />
        <div style={{ width: "100%", height: 12, background: "rgba(0,0,0,0.08)", borderRadius: 4, marginBottom: 6 }} />
        <div style={{ width: "80%", height: 12, background: "rgba(0,0,0,0.08)", borderRadius: 4 }} />
      </div>
    </div>
  );
}

// Source key → human-readable section label, in the same order the LLM is
// asked to produce rationale bullets in (see jobs/ai_agent_followup.py).
const RATIONALE_SECTIONS = [
  ["platform_events", "Platform events"],
  ["marketing_emails_sent", "Marketing emails sent"],
  ["prior_personal_conversation", "Prior personal conversation"],
];

// Crisp 1-line summary of what we know about the homeowner: name (if known),
// phone (if known), and which CRM the contact lives in. Surfaces the lookup
// the ai_agent_followup batch does against production.contacts_merged so the
// reviewing agent can decide whether to send.
const CRM_LABELS = {
  agent_box_contact: "Agentbox",
  agent_box_enquiry: "Agentbox",
  property_me_contact: "PropertyMe",
  rex_contact: "Rex",
  vault_contact: "Vault",
  boxdice_contact: "Box+Dice",
};

function ContactDetails({ item }) {
  const c = item?.metadata?.contact || {};
  const phone = item?.contact_phone || c.phone;
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  const crmRaw = c.crm_source;
  const crmLabel = crmRaw ? (CRM_LABELS[crmRaw] || crmRaw) : null;
  if (!phone && !name && !crmLabel) return null;
  return (
    <div style={{
      display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6,
      fontSize: 11, color: "#6b7280", marginBottom: 10,
    }}>
      {name && (
        <span style={{ color: "#374151", fontWeight: 600 }}>{name}</span>
      )}
      {phone && (
        <span style={{
          padding: "2px 8px", borderRadius: 999,
          background: "#eef2ff", color: "#3730a3", fontWeight: 600,
        }}>
          {phone}
        </span>
      )}
      {crmLabel && (
        <span style={{
          padding: "2px 8px", borderRadius: 999,
          background: "#ecfdf5", color: "#065f46", fontWeight: 600,
        }}>
          CRM: {crmLabel}
        </span>
      )}
    </div>
  );
}

function ContextSnapshot({ snapshot, label = "Show context the AI used" }) {
  const [open, setOpen] = useState(false);
  if (!snapshot || typeof snapshot !== "object") return null;

  const rationale = snapshot.rationale && typeof snapshot.rationale === "object"
    ? snapshot.rationale : null;
  const sections = RATIONALE_SECTIONS
    .map(([key, title]) => {
      const bullets = Array.isArray(rationale?.[key])
        ? rationale[key].filter(b => typeof b === "string" && b.trim())
        : [];
      return { key, title, bullets };
    })
    .filter(s => s.bullets.length > 0);

  if (sections.length === 0) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12,
          color: "#6b7280", background: "none", border: "none", padding: 0,
          cursor: "pointer", fontWeight: 500,
        }}
      >
        {open ? <FaChevronUp size={9} /> : <FaChevronDown size={9} />}
        {open ? "Hide context" : label}
      </button>
      {open && (
        <div style={{
          marginTop: 8, padding: 10, background: "#f9fafb",
          borderLeft: "3px solid #e5e7eb", borderRadius: 4,
          fontSize: 12, color: "#374151", maxHeight: 280, overflowY: "auto",
        }}>
          {sections.map((s, i) => (
            <div key={s.key} style={{ marginBottom: i === sections.length - 1 ? 0 : 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4, fontSize: 11 }}>
                {s.title}
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                {s.bullets.map((b, j) => (
                  <li key={j}>{b}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// One card in the Needs Review queue. Same shape regardless of kind, with a
// header chip (Reply vs Outbound) and (for replies) an inline preview of the
// homeowner's most recent message.
// Memoized so editing one card's subject/body (which lifts state up to the
// parent via onUpdateDraft) does NOT re-render every other card in the
// Needs Review list. Sibling cards' `item` reference is unchanged so memo
// short-circuits them — which makes Send/Dismiss/channel-toggle buttons
// respond instantly even with a long queue on screen.
const DraftCard = React.memo(function DraftCard({
  item,
  demo,              // new: card is showing seeded sample data (regenerate disabled)
  onActioned,        // legacy callback — still used by ThreadView for full refresh
  onRemove,          // new: splice this id locally instead of refetching
  onReplace,         // new: swap this id for a fresh draft locally after Regenerate
  onRegenerated,     // new: fallback refetch when onReplace can't run (no hydrated draft)
  onUpdateDraft,     // new: lift subject/body up so Send All sees latest edits
  selected,
  onToggleSelected,  // new: per-card checkbox bound to parent selection set
  hideOwnership,     // new: ThreadView already shows the toggle in its header
  hideRegenerate,    // new: ThreadView surfaces Regenerate at the top instead
  isStreaming,       // new: this card represents an in-flight streaming compose
  setToast,
  onOpenThread,
  onOpenConversationModal, // new: opens the AI Conversation history modal overlay
  onOpenSearches,    // new: opens the per-contact Property Searches modal
  onOpenActivity,    // new: opens the per-contact Activity timeline modal
  onOwnershipChange,
  balance,           // new: shared platform credit balance for SMS gating
  onBalanceChange,   // new: tell parent to refetch /billing/balance after a send
  notesPopover,      // new: { openHover, openPinned, closeHover } from parent's useHandoverNotesPopover
}) {
  const isMobile = useIsMobile();
  // Drafts now carry per-channel content side-by-side: an email subject +
  // body and a separate SMS body. The agent can flip between them at send
  // time when the contact has both an email and a phone on file. We keep
  // edits to each version independent so toggling channel doesn't blow
  // away unsaved text.
  const initialEmailSubject = (item.email_subject ?? item.subject ?? "") || "";
  const initialEmailBody = (item.email_body
    ?? ((item.channel === "email" || !item.channel) ? item.body : "")
    ?? "") || "";
  const initialSmsBody = (item.sms_body
    ?? (item.channel === "sms" ? item.body : "")
    ?? "") || "";
  // For Call channel: a single editable note field. Defaults to the
  // AI-generated talking points (a short script the agent can read off
  // on the call). The agent edits / appends the outcome and the saved
  // text becomes a contact_handover_note.
  const initialCallNote = (item.talking_points
    ?? item.email_body
    ?? item.sms_body
    ?? item.body
    ?? "") || "";

  const controlled = typeof onUpdateDraft === "function";
  const [localEmailSubject, setLocalEmailSubject] = useState(initialEmailSubject);
  const [localEmailBody, setLocalEmailBody] = useState(initialEmailBody);
  const [localSmsBody, setLocalSmsBody] = useState(initialSmsBody);
  const [localCallNote, setLocalCallNote] = useState(initialCallNote);

  const emailSubject = controlled ? (item.email_subject ?? item.subject ?? "") : localEmailSubject;
  const emailBody = controlled ? (item.email_body ?? ((item.channel === "email" || !item.channel) ? (item.body ?? "") : "")) : localEmailBody;
  const smsBody = controlled ? (item.sms_body ?? (item.channel === "sms" ? (item.body ?? "") : "")) : localSmsBody;
  const callNote = controlled
    ? (item.call_note ?? initialCallNote)
    : localCallNote;

  const setEmailSubject = (v) => controlled
    ? onUpdateDraft(item.id, { email_subject: v, subject: v })
    : setLocalEmailSubject(v);
  const setEmailBody = (v) => controlled
    ? onUpdateDraft(item.id, { email_body: v, ...(item.channel !== "sms" && item.channel !== "call" ? { body: v } : {}) })
    : setLocalEmailBody(v);
  const setSmsBody = (v) => controlled
    ? onUpdateDraft(item.id, { sms_body: v, ...(item.channel === "sms" ? { body: v } : {}) })
    : setLocalSmsBody(v);
  const setCallNote = (v) => controlled
    ? onUpdateDraft(item.id, { call_note: v })
    : setLocalCallNote(v);

  const [sending, setSending] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [showBuyCredit, setShowBuyCredit] = useState(false);
  // Regenerate — mirrors the "Generate Follow Up" UX in ThreadView so a
  // user can ask the AI for a fresh draft directly from the card in the
  // Needs Review queue without drilling into the thread. Posts to the
  // same /ai-conversations/generate-followup endpoint, which dismisses
  // the existing pending draft and inserts a new one. We then call
  // onActioned() so the parent refetches and the new draft replaces this
  // card in-place.
  const [regenerating, setRegenerating] = useState(false);
  const [regenProgress, setRegenProgress] = useState(0);
  const [showRegenPopover, setShowRegenPopover] = useState(false);
  const [regenExtra, setRegenExtra] = useState("");
  // Dismiss popover — mirrors the Regenerate popover UX so the agent can
  // attach an optional handover note explaining why they're dismissing.
  // The note is saved via /agent-add-contact-handover-note (the same
  // endpoint AIHandoverNotesPopover uses) so it shows up alongside every
  // other note on the contact.
  const [showDismissPopover, setShowDismissPopover] = useState(false);
  const [dismissNote, setDismissNote] = useState("");
  const regenTimer = useRef(null);
  const stopRegenProgress = useCallback((complete) => {
    if (regenTimer.current) {
      clearInterval(regenTimer.current);
      regenTimer.current = null;
    }
    if (complete) {
      setRegenProgress(100);
      setTimeout(() => setRegenProgress(0), 400);
    } else {
      setRegenProgress(0);
    }
  }, []);
  useEffect(() => () => {
    if (regenTimer.current) clearInterval(regenTimer.current);
  }, []);
  const regenerate = async () => {
    if (regenerating) return;
    if (demo) {
      setShowRegenPopover(false);
      setToast?.({ severity: "info", message: "This is sample data — regenerate is disabled in the demo." });
      return;
    }
    setRegenerating(true);
    setRegenProgress(0);
    const startTs = Date.now();
    const DURATION_MS = 30000;
    if (regenTimer.current) clearInterval(regenTimer.current);
    regenTimer.current = setInterval(() => {
      const ratio = Math.min((Date.now() - startTs) / DURATION_MS, 1);
      setRegenProgress(Math.min(95, ratio * 100 * (2 - ratio)));
    }, 200);

    // Stream the new draft into the editable inputs on this card. Requires
    // controlled-mode (onUpdateDraft must be wired), which is always true
    // when Regenerate is shown — the only surface that renders the button
    // is the Needs Review queue, which manages draft state in the page
    // root. Fall back to a single-shot request when uncontrolled so we
    // don't silently no-op in unusual setups.
    if (controlled) {
      // Clear the existing text so the stream visibly "writes" into empty
      // fields. We only clear the active channel's main body to avoid
      // wiping a subject the agent may not notice has been replaced.
      onUpdateDraft(item.id, {
        email_subject: "", subject: "",
        email_body: "", body: "",
        sms_body: "",
        talking_points: "", call_note: "",
      });
      let sawAnyDelta = false;
      let finalDraft = null;
      let streamErr = null;
      await streamGenerateFollowup({
        contactKey: item.contact_key || (item.contact_email || "").toLowerCase(),
        extraInstructions: regenExtra,
        onPatch: (patch) => {
          sawAnyDelta = true;
          // Keep `body` in sync with the active channel so uncontrolled
          // consumers (and the Send All payload builder) see the right
          // text. `email_body` and `sms_body` are already explicit fields
          // and round-trip via the queue row directly.
          const extra = {};
          if (typeof patch.email_body === "string" && (item.channel !== "sms" && item.channel !== "call")) {
            extra.body = patch.email_body;
          }
          if (typeof patch.sms_body === "string" && item.channel === "sms") {
            extra.body = patch.sms_body;
          }
          onUpdateDraft(item.id, { ...patch, ...extra });
        },
        onDone: (d) => { finalDraft = d; },
        onError: (e) => { streamErr = e; },
      });

      if (streamErr) {
        setToast?.({ severity: "error", message: streamErr.message || "Failed to regenerate follow-up." });
        stopRegenProgress(false);
        setRegenerating(false);
        return;
      }
      setToast?.({ severity: "success", message: "Follow-up draft regenerated." });
      setRegenExtra("");
      stopRegenProgress(true);
      if (finalDraft && typeof onReplace === "function") {
        // Swap to the hydrated server row so the new queue id (Send /
        // Dismiss target) replaces the dismissed-row id. The streamed
        // text content should match the persisted row, so the textareas
        // don't visibly flicker.
        onReplace(item.id, finalDraft);
      } else if (typeof onRegenerated === "function") {
        onRegenerated();
      } else {
        onActioned?.();
      }
      if (sawAnyDelta === false && !finalDraft) {
        setToast?.({ severity: "error", message: "No content returned from AI." });
      }
      setRegenerating(false);
      return;
    }

    // --- Uncontrolled fallback (legacy non-streaming) ----------------
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/ai-conversations/generate-followup`,
        {
          contact_key: item.contact_key || (item.contact_email || "").toLowerCase(),
          extra_instructions: regenExtra.trim() || undefined,
        },
        { "Content-Type": "application/json" }
      );
      if (res.data && res.data.status === "success") {
        setToast?.({ severity: "success", message: "Follow-up draft regenerated." });
        setRegenExtra("");
        stopRegenProgress(true);
        if (typeof onReplace === "function" && res.data.draft) {
          onReplace(item.id, res.data.draft);
        } else if (typeof onRegenerated === "function") {
          onRegenerated();
        } else {
          onActioned?.();
        }
      } else {
        setToast?.({ severity: "error", message: res.data?.message || "Failed to regenerate follow-up." });
        stopRegenProgress(false);
      }
    } catch (e) {
      setToast?.({ severity: "error", message: e?.response?.data?.message || "Failed to regenerate follow-up." });
      stopRegenProgress(false);
    } finally {
      setRegenerating(false);
    }
  };
  // Local optimistic ownership flag — defaults to true (matches the
  // server-side COALESCE) when the row predates the contact_ai_settings row.
  const [aiOwns, setAiOwns] = useState(item.ai_owns_relationship !== false);
  useEffect(() => { setAiOwns(item.ai_owns_relationship !== false); }, [item.ai_owns_relationship]);
  const isReply = (item.kind || "outbound") === "reply";

  // Channel handling. Drafts default to whatever channel they were queued
  // with (the LLM picks the preferred one for outbound drafts); when the
  // contact has both an email and a phone the agent can flip between them.
  // Call is always available as a third option (the agent can log a call
  // note for any contact, regardless of phone-on-file — they may have a
  // number from elsewhere).
  const queuedChannel = (item.channel === "sms" || item.channel === "call") ? item.channel : "email";
  const hasEmail = !!(item.contact_email || "").trim();
  const hasPhone = !!(item.contact_phone || "").trim();

  // When the parent doesn't manage draft state (e.g. ThreadView's pinned
  // pending drafts in the AI Conversation modal), keep the chosen channel
  // in local state so the Email/SMS/Call switcher actually flips the UI.
  const [localChannel, setLocalChannel] = useState(queuedChannel);
  const setChannel = (next) => {
    if (controlled) onUpdateDraft(item.id, { channel: next });
    else setLocalChannel(next);
  };
  const channel = (() => {
    const c = controlled ? item.channel : localChannel;
    if (c === "sms" || c === "call") return c;
    return "email";
  })();
  const isSms = channel === "sms";
  const isCall = channel === "call";

  // Keep parent state in sync if a draft loads with channel='sms' but no
  // phone (shouldn't happen for new drafts, but legacy rows might).
  useEffect(() => {
    if (controlled && item.channel === "sms" && !hasPhone && hasEmail) {
      onUpdateDraft(item.id, { channel: "email" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Available channels offered by the toggle. Email needs an address,
  // SMS and Call both need a phone number on file.
  const availableChannels = [];
  if (hasEmail) availableChannels.push("email");
  if (hasPhone) availableChannels.push("sms");
  if (hasPhone) availableChannels.push("call");
  const canSwitchChannel = availableChannels.length > 1;

  const finishAction = () => onRemove ? onRemove(item.id) : onActioned?.();
  const showCheckbox = typeof onToggleSelected === "function";

  const send = async () => {
    // Demo: simulate the send locally — remove the card, no credit gate, no
    // network. Keeps the sample queue interactive without touching the backend.
    if (demo) {
      setToast({ severity: "success", message: "Sample data — nothing was actually sent." });
      finishAction();
      return;
    }
    const cleanSubject = (emailSubject || "").trim();
    const cleanEmailBody = (emailBody || "").trim();
    const cleanSmsBody = (smsBody || "").trim();
    const cleanCallNote = (callNote || "").trim();
    if (isCall) {
      if (!cleanCallNote) {
        setToast({ severity: "error", message: "Add a call note before saving." });
        return;
      }
    } else if (isSms) {
      if (!cleanSmsBody) {
        setToast({ severity: "error", message: "Message body is required." });
        return;
      }
      if (!hasPhone) {
        setToast({ severity: "error", message: "No mobile number on file for this contact." });
        return;
      }
      // Same client-side credit gate as pages/PropertySearchDetail.js — uses
      // the platform's own balance (production.agent_balances via
      // /billing/balance), NOT Bird's balance. Pop the top-up modal before
      // we ever round-trip to the server.
      const cost = smsCost(cleanSmsBody);
      const have = Number(balance?.balance_amount ?? 0);
      if (cost > have + 1e-9) {
        setShowBuyCredit(true);
        return;
      }
    } else if (!cleanSubject || !cleanEmailBody) {
      setToast({ severity: "error", message: "Subject and body are required." });
      return;
    }
    setSending(true);
    try {
      let payload;
      if (isCall) {
        payload = {
          channel: "call",
          note: cleanCallNote,
        };
      } else if (isSms) {
        payload = { channel: "sms", body: cleanSmsBody, contact_phone: item.contact_phone };
      } else {
        payload = { channel: "email", subject: cleanSubject, body: cleanEmailBody };
      }
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/ai-outbound-queue/${item.id}/send`,
        payload,
        { "Content-Type": "application/json" }
      );
      if (res.data && res.data.status === "success") {
        let msg;
        if (isCall) {
          msg = "Call note saved";
        } else {
          const target = isSms ? (item.contact_phone || "homeowner") : (item.contact_email || "homeowner");
          msg = `Sent to ${target}`;
        }
        setToast({ severity: "success", message: msg });
        if (isSms) onBalanceChange?.();
        finishAction();
      } else {
        setToast({ severity: "error", message: res.data?.message || (isCall ? "Save failed" : "Send failed") });
      }
    } catch (e) {
      setToast({ severity: "error", message: e?.response?.data?.message || (isCall ? "Save failed" : "Send failed") });
    } finally {
      setSending(false);
    }
  };

  const dismiss = async (note) => {
    // Demo: drop the card locally, no note save, no network.
    if (demo) {
      setShowDismissPopover(false);
      setToast({ severity: "success", message: "Dismissed (sample data)." });
      finishAction();
      return;
    }
    const trimmedNote = (note || "").trim();
    setDismissing(true);
    try {
      // Save the optional handover note first — if it fails we surface the
      // error and abort, so the user can retry without losing the draft.
      if (trimmedNote) {
        const identity = {
          contact_email: (item.contact_email || "").trim().toLowerCase() || null,
          contact_mobile: (item.contact_phone || "").trim() || null,
        };
        if (!identity.contact_email && !identity.contact_mobile) {
          setToast({ severity: "error", message: "Cannot save note — this draft has no contact email or mobile." });
          setDismissing(false);
          return;
        }
        try {
          await handleTokenAndPostRequest(
            `${window.api_host}/agent-add-contact-handover-note`,
            { ...identity, note: trimmedNote },
          );
        } catch (e) {
          setToast({ severity: "error", message: e?.response?.data?.message || "Failed to save note" });
          setDismissing(false);
          return;
        }
      }
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/ai-outbound-queue/${item.id}/dismiss`,
        {},
        { "Content-Type": "application/json" }
      );
      if (res.data && res.data.status === "success") {
        setToast({ severity: "success", message: trimmedNote ? "Dismissed — note saved" : "Dismissed" });
        setDismissNote("");
        finishAction();
      } else {
        setToast({ severity: "error", message: res.data?.message || "Failed to dismiss" });
      }
    } catch (e) {
      setToast({ severity: "error", message: e?.response?.data?.message || "Failed to dismiss" });
    } finally {
      setDismissing(false);
    }
  };

  const triggers = Array.isArray(item.trigger_names) ? item.trigger_names : [];
  const kindChipStyle = isReply
    ? { background: "#dbeafe", color: "#1e40af" }
    : { background: "#ede9fe", color: "#6d28d9" };
  const channelChipStyle = isCall
    ? { background: "#fef3c7", color: "#92400e" }
    : isSms
      ? { background: "#dcfce7", color: "#166534" }
      : { background: "#e0f2fe", color: "#075985" };
  const channelChipIcon = isCall
    ? <FaPhone size={9} />
    : isSms
      ? <FaCommentDots size={9} />
      : <FaEnvelope size={9} />;
  const channelChipLabel = isCall ? "CALL" : isSms ? "SMS" : "EMAIL";

  return (
    <div style={{
      padding: 16, background: "#fff", border: "1px solid #e5e7eb",
      borderRadius: 12, marginBottom: 12,
    }}>
      {/* Row 1 — identity: checkbox, type/channel chips, contact, score,
          AI/Me toggle pinned right. Keeps the "who is this draft about"
          info together and gives the ownership toggle a stable home. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        {showCheckbox && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelected(item.id)}
            title="Include in Send All"
            style={{ width: 16, height: 16, accentColor: "#7c3aed", cursor: "pointer", margin: 0 }}
          />
        )}
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          fontSize: 11, padding: "3px 10px", borderRadius: 999,
          fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0,
          ...kindChipStyle,
        }}>
          {isReply ? <FaReply size={9} /> : <FaSend size={9} />}
          {isReply ? "REPLY" : "OUTBOUND"}
        </span>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          fontSize: 11, padding: "3px 10px", borderRadius: 999,
          fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0,
          ...channelChipStyle,
        }}>
          {channelChipIcon}
          {channelChipLabel}
        </span>
        <button
          onClick={() => onOpenThread?.(item.contact_key || (item.contact_email || "").toLowerCase())}
          style={{
            background: "none", border: "none", padding: 0, cursor: "pointer",
            fontSize: 13, fontWeight: 600, color: "#111827",
            display: "inline-flex", alignItems: "center", gap: 6,
            minWidth: 0, overflow: "hidden",
          }}
          title="Open full conversation"
        >
          <FaRobot style={{ color: "#7c3aed", flexShrink: 0 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {item.contact_email || item.contact_key}
          </span>
        </button>
        <LeadScoreBadge
          score={item.lead_score}
          components={item.lead_components}
          lastEventAt={item.lead_last_event_at}
        />
        {!hideOwnership && (
          <div style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
            <AIOwnerToggle
              aiOwns={aiOwns}
              size="sm"
              onChange={(next) => postOwnership({
                contactKey: item.contact_key,
                contactEmail: item.contact_email,
                next,
                applyLocal: (v) => { setAiOwns(v); onOwnershipChange?.(item.contact_key, v); },
                current: aiOwns,
                setToast,
              })}
            />
          </div>
        )}
      </div>

      {/* Row 2 — actions + meta: icon buttons (AI Conv, Searches, Activity,
          Notes) on the left, trigger / version chips and timestamp on the
          right. Separating these from the identity row keeps the score
          and toggle visually anchored instead of floating amid chips. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        {onOpenConversationModal && (
          <Tooltip arrow placement="top" title="Open AI Conversation history">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const key = item.contact_key || (item.contact_email || "").toLowerCase();
                if (!key) return;
                onOpenConversationModal(key);
              }}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 32, height: 32, borderRadius: 8, padding: 0,
                background: "#fff", border: "1px solid #e5e7eb",
                color: "#6b7280", cursor: "pointer",
                transition: "background 0.12s, border-color 0.12s, color 0.12s",
              }}
            >
              <FaCommentDots style={{ fontSize: 14 }} />
            </button>
          </Tooltip>
        )}
        {onOpenSearches && (item.contact_email || item.contact_phone) && (
          <Tooltip arrow placement="top" title="Past property searches & estimates">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const q = item.contact_email || item.contact_phone;
                if (!q) return;
                onOpenSearches(q, item);
              }}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 32, height: 32, borderRadius: 8, padding: 0,
                background: "#fff", border: "1px solid #e5e7eb",
                color: "#6b7280", cursor: "pointer",
                transition: "background 0.12s, border-color 0.12s, color 0.12s",
              }}
            >
              <FaHome style={{ fontSize: 14 }} />
            </button>
          </Tooltip>
        )}
        {onOpenActivity && item.contact_email && (
          <Tooltip arrow placement="top" title="View activity timeline">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpenActivity(item);
              }}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 32, height: 32, borderRadius: 8, padding: 0,
                background: "#fff", border: "1px solid #e5e7eb",
                color: "#6b7280", cursor: "pointer",
                transition: "background 0.12s, border-color 0.12s, color 0.12s",
              }}
            >
              <FaChartLine style={{ fontSize: 14 }} />
            </button>
          </Tooltip>
        )}
        {notesPopover && (
          <HandoverNotesButton
            contact={{
              email: item.contact_email,
              clean_mobile: item.contact_phone,
            }}
            openHover={notesPopover.openHover}
            openPinned={notesPopover.openPinned}
            closeHover={notesPopover.closeHover}
            iconOnly
          />
        )}
        <div style={{
          marginLeft: "auto",
          display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap",
          justifyContent: "flex-end",
        }}>
          {triggers.map(tn => (
            <span key={tn} style={{
              fontSize: 10, padding: "2px 8px", borderRadius: 999,
              background: "#f3f4f6", color: "#4b5563", fontWeight: 600,
              whiteSpace: "nowrap", flexShrink: 0,
            }}>{tn}</span>
          ))}
          {item.draft_version > 1 && (
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 999, background: "#fef3c7", color: "#92400e", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
              v{item.draft_version}
            </span>
          )}
          <span style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap" }}>
            {isReply ? "drafted" : "queued"} {fmtRelative(item.created_at)}
          </span>
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: isReply ? 6 : 10 }}>
        From: {item.ai_agent_name ? `${item.ai_agent_name} <${item.ai_agent_email}>` : item.ai_agent_email}
      </div>
      {/* Contact tags applied on /contacts \u2014 mirror the chip strip from
          the contact row so the agent can see funnel stage / flags at a
          glance while reviewing the draft. Read-only here; the picker
          lives in ThreadView (drill-in) and on /contacts. */}
      {Array.isArray(item.contact_tags) && item.contact_tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
          {item.contact_tags.map((t) => (
            <ContactTagChip key={t.slug} tag={t} />
          ))}
        </div>
      )}
      <ContactDetails item={item} />

      {isReply && item.user_message_text && (
        <div style={{
          marginBottom: 12, padding: "10px 12px",
          background: "#f9fafb", borderLeft: "3px solid #94a3b8", borderRadius: 4,
          fontSize: 13, color: "#374151", lineHeight: 1.5, whiteSpace: "pre-wrap",
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
            Their message
          </div>
          {item.user_message_text}
        </div>
      )}

      {!isCall && !isSms && (
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
            Subject
          </label>
          <input
            type="text"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            style={{
              width: "100%", padding: "8px 10px", borderRadius: 8,
              border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box",
            }}
          />
        </div>
      )}

      {isCall ? (
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
            Call note
          </label>
          <textarea
            value={callNote}
            onChange={(e) => setCallNote(e.target.value)}
            rows={8}
            placeholder="Talking points, then what was said and any next steps…"
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8,
              border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box",
              fontFamily: "inherit", resize: "vertical", minHeight: 160,
              background: "#fffbeb",
            }}
          />
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
            Pre-filled with the AI’s suggested talking points. Edit and add the call outcome — this becomes a handover note for this contact.
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
            {isSms ? "Message" : (isReply ? "Reply" : "Body")}
          </label>
          <textarea
            value={isSms ? smsBody : emailBody}
            onChange={(e) => isSms ? setSmsBody(e.target.value) : setEmailBody(e.target.value)}
            rows={isSms ? 4 : (isReply ? 6 : 8)}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8,
              border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box",
              fontFamily: "inherit", resize: "vertical",
              minHeight: isSms ? 80 : 120,
            }}
          />
          <div style={{
            fontSize: 11, color: "#9ca3af", marginTop: 4,
            display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap",
          }}>
            <span>
              {isSms
                ? "Plain text only. Sent from your AI mobile number."
                : "Your AI Agent's signature, photo, and agency logo are appended automatically."}
            </span>
            {isSms && (
              <span style={{ color: smsBody.length > 320 ? "#dc2626" : "#9ca3af" }}>
                {smsBody.length} chars · {Math.max(1, Math.ceil(smsBody.length / 160))} segment{smsBody.length > 160 ? "s" : ""}
                {" · "}A${smsCost(smsBody).toFixed(2)} (A${SMS_PRICE_PER_PART.toFixed(2)}/part)
              </span>
            )}
          </div>
        </div>
      )}

      {canSwitchChannel && (
        <div style={{ marginBottom: 10, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>Send via:</span>
          {availableChannels.includes("email") && (
            <button
              type="button"
              onClick={() => setChannel("email")}
              style={{
                fontSize: 12, padding: "5px 10px", borderRadius: 999,
                border: `1px solid ${channel === "email" ? "#0a9fa6" : "#d1d5db"}`,
                background: channel === "email" ? "#ecfeff" : "#fff",
                color: channel === "email" ? "#0a9fa6" : "#374151",
                fontWeight: 600, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              <FaEnvelope size={10} /> Email
            </button>
          )}
          {availableChannels.includes("sms") && (
            <button
              type="button"
              onClick={() => setChannel("sms")}
              style={{
                fontSize: 12, padding: "5px 10px", borderRadius: 999,
                border: `1px solid ${isSms ? "#16a34a" : "#d1d5db"}`,
                background: isSms ? "#dcfce7" : "#fff",
                color: isSms ? "#166534" : "#374151",
                fontWeight: 600, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              <FaCommentDots size={10} /> SMS
            </button>
          )}
          {availableChannels.includes("call") && (
            <button
              type="button"
              onClick={() => setChannel("call")}
              style={{
                fontSize: 12, padding: "5px 10px", borderRadius: 999,
                border: `1px solid ${isCall ? "#d97706" : "#d1d5db"}`,
                background: isCall ? "#fef3c7" : "#fff",
                color: isCall ? "#92400e" : "#374151",
                fontWeight: 600, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              <FaPhone size={10} /> Call
            </button>
          )}
        </div>
      )}

      <ContextSnapshot snapshot={item.history_snapshot} />

      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <button
          onClick={send}
          disabled={sending || dismissing || isStreaming
            || (isCall
                ? !callNote.trim()
                : (isSms ? !smsBody.trim() : (!emailBody.trim() || !emailSubject.trim())))}
          title={isStreaming ? "Waiting for the AI to finish writing…" : undefined}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 16px", borderRadius: 8, border: "none",
            background: (sending || isStreaming
                || (isCall
                    ? !callNote.trim()
                    : (isSms ? !smsBody.trim() : (!emailBody.trim() || !emailSubject.trim()))))
              ? "#a78bfa" : "linear-gradient(90deg, #7c3aed, #5b21b6)",
            color: "#fff", fontWeight: 600, fontSize: 13,
            cursor: (sending || dismissing || isStreaming
                || (isCall
                    ? !callNote.trim()
                    : (isSms ? !smsBody.trim() : (!emailBody.trim() || !emailSubject.trim()))))
              ? "default" : "pointer",
          }}
        >
          {isCall ? <FaStickyNote size={11} /> : <FaPaperPlane size={11} />}
          {sending
            ? (isCall ? "Saving…" : "Sending…")
            : (isCall ? "Save" : "Send")}
        </button>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <button
            onClick={() => {
              if (sending || dismissing || regenerating || isStreaming) return;
              setShowDismissPopover(v => !v);
            }}
            disabled={sending || dismissing || regenerating || isStreaming}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 16px", borderRadius: 8,
              border: "1px solid #d1d5db", background: "#fff",
              color: "#374151", fontWeight: 600, fontSize: 13,
              cursor: (sending || dismissing || regenerating || isStreaming) ? "default" : "pointer",
            }}
          >
            <FaTimes size={11} /> {dismissing ? "Dismissing…" : "Dismiss"}
          </button>
          {showDismissPopover && !dismissing && (
            <>
              <div
                onClick={() => setShowDismissPopover(false)}
                style={{ position: "fixed", inset: 0, zIndex: 40, background: "transparent" }}
              />
              <div
                style={{
                  position: "absolute", bottom: "calc(100% + 6px)", left: 0, zIndex: 41,
                  width: 320, maxWidth: "calc(100vw - 32px)",
                  background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                  padding: 12,
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setShowDismissPopover(false);
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                    e.preventDefault();
                    setShowDismissPopover(false);
                    dismiss(dismissNote);
                  }
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                  Dismiss note (optional)
                </div>
                <textarea
                  value={dismissNote}
                  onChange={(e) => setDismissNote(e.target.value.slice(0, 2000))}
                  rows={3}
                  maxLength={2000}
                  autoFocus
                  placeholder="Why are you dismissing? (saved as a note on this contact)"
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: "8px 10px", borderRadius: 8,
                    border: "1px solid #d1d5db", fontSize: 13,
                    fontFamily: "inherit", color: "#111827", background: "#fff",
                    resize: "vertical", outline: "none",
                  }}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                  <button
                    onClick={() => setShowDismissPopover(false)}
                    style={{
                      padding: "6px 10px", borderRadius: 8,
                      border: "1px solid #d1d5db", background: "#fff",
                      color: "#374151", fontWeight: 600, fontSize: 12, cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowDismissPopover(false);
                      dismiss(dismissNote);
                    }}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "6px 10px", borderRadius: 8,
                      border: "1px solid #374151", background: "#374151",
                      color: "#fff", fontWeight: 600, fontSize: 12, cursor: "pointer",
                    }}
                  >
                    <FaTimes size={11} /> {dismissNote.trim() ? "Dismiss & save note" : "Dismiss"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        {/* Regenerate — opens a small popover for optional extra
            instructions, then fires /ai-conversations/generate-followup.
            Same UX as ThreadView's "Generate Follow Up" button so the
            two surfaces feel consistent. Hidden when rendered inside
            ThreadView, which exposes its own top-level Regenerate button. */}
        {!hideRegenerate && (
        <div style={{ position: "relative", flexShrink: 0 }}>
          <button
            onClick={() => {
              if (regenerating || sending || dismissing) return;
              setShowRegenPopover(v => !v);
            }}
            disabled={regenerating || sending || dismissing}
            title="Have the AI draft a fresh follow-up for this contact"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 16px", borderRadius: 8,
              border: `1px solid ${regenerating ? "#0f766e" : "#0d9488"}`,
              background: regenerating ? "#0f766e" : "#0d9488",
              color: "#fff", fontWeight: 600, fontSize: 13,
              cursor: (regenerating || sending || dismissing) ? "default" : "pointer",
              opacity: 1,
              transition: "background 0.12s ease, border-color 0.12s ease",
            }}
          >
            {regenerating ? (
              <>
                {/* Keyframe injected inline so the spinner animates even
                    when the page-level SKELETON_KEYFRAMES block isn't
                    mounted (DraftCard is already past its loading state). */}
                <style>{`@keyframes ai-followup-spin { to { transform: rotate(360deg); } }`}</style>
                <span
                  aria-hidden
                  style={{
                    width: 11, height: 11, borderRadius: "50%",
                    border: "2px solid rgba(255,255,255,0.4)",
                    borderTopColor: "#fff",
                    display: "inline-block",
                    animation: "ai-followup-spin 0.7s linear infinite",
                  }}
                />
              </>
            ) : (
              <FaMagic size={11} />
            )}
            {regenerating ? "Regenerating…" : "Regenerate"}
          </button>
          {showRegenPopover && !regenerating && (
            <>
              <div
                onClick={() => setShowRegenPopover(false)}
                style={{ position: "fixed", inset: 0, zIndex: 40, background: "transparent" }}
              />
              <div
                style={{
                  position: "absolute", bottom: "calc(100% + 6px)", right: 0, zIndex: 41,
                  width: 320, maxWidth: "calc(100vw - 32px)",
                  background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                  padding: 12,
                }}
                onKeyDown={(e) => { if (e.key === "Escape") setShowRegenPopover(false); }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                  Extra instructions (optional)
                </div>
                <textarea
                  value={regenExtra}
                  onChange={(e) => setRegenExtra(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  autoFocus
                  placeholder="e.g. mention our open home on Saturday"
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: "8px 10px", borderRadius: 8,
                    border: "1px solid #d1d5db", fontSize: 13,
                    fontFamily: "inherit", color: "#111827", background: "#fff",
                    resize: "vertical", outline: "none",
                  }}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                  <button
                    onClick={() => setShowRegenPopover(false)}
                    style={{
                      padding: "6px 10px", borderRadius: 8,
                      border: "1px solid #d1d5db", background: "#fff",
                      color: "#374151", fontWeight: 600, fontSize: 12, cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowRegenPopover(false);
                      regenerate();
                    }}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "6px 10px", borderRadius: 8,
                      border: "1px solid #0d9488", background: "#0d9488",
                      color: "#fff", fontWeight: 600, fontSize: 12, cursor: "pointer",
                    }}
                  >
                    <FaMagic size={11} /> Regenerate
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        )}
      </div>
      {regenerating && (
        <div style={{ marginTop: 10 }}>
          <div
            style={{
              width: "100%", height: 4, background: "#e5e7eb",
              borderRadius: 2, overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${regenProgress}%`,
                background: "linear-gradient(90deg, #0d9488, #14b8a6)",
                borderRadius: 2,
                transition: regenProgress === 100 ? "width 0.3s ease" : "width 0.4s linear",
              }}
            />
          </div>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6, textAlign: "center" }}>
            {regenProgress < 95
              ? "Regenerating a follow-up — usually takes about 20–30 seconds…"
              : "Almost there…"}
          </div>
        </div>
      )}
      {showBuyCredit && (
        <BuyExtraUsageModal
          onClose={() => setShowBuyCredit(false)}
          defaultAmount={Math.max(10, Math.ceil(smsCost(smsBody)))}
          reason={`You don't have enough extra usage to send this SMS (cost A$${smsCost(smsBody).toFixed(2)}, balance A$${Number(balance?.balance_amount ?? 0).toFixed(2)}). Top up to continue.`}
        />
      )}
    </div>
  );
});

// One row in the All Conversations tab. Click → opens thread drill-in.
// Rendered as a div (not a <button>) because the AI/Me toggle nests
// interactive children, which a <button> wrapper can't legally contain.
//
// Memoized so parent state changes (tab switches, toasts, search input,
// kindFilter, selection set, sibling rows being edited) don't re-render
// every row in the list — which on the Needs Review / All Conversations
// tabs noticeably delayed every button click before any visual feedback.
const ConversationRow = React.memo(function ConversationRow({ item, onOpen, setToast, onOwnershipChange }) {
  const isMobile = useIsMobile();
  const [aiOwns, setAiOwns] = useState(item.ai_owns_relationship !== false);
  useEffect(() => { setAiOwns(item.ai_owns_relationship !== false); }, [item.ai_owns_relationship]);
  const open = () => onOpen(item.contact_key);
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); }
  };
  const ownerToggle = (
    <AIOwnerToggle
      aiOwns={aiOwns}
      size="sm"
      onChange={(next) => postOwnership({
        contactKey: item.contact_key,
        contactEmail: item.contact_email,
        next,
        applyLocal: (v) => { setAiOwns(v); onOwnershipChange?.(item.contact_key, v); },
        current: aiOwns,
        setToast,
      })}
    />
  );
  const time = (
    <span style={{ fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap", flexShrink: 0 }}>
      {fmtRelative(item.last_activity_at)}
    </span>
  );
  const pending = item.pending_drafts > 0 && (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999,
      background: "#fef3c7", color: "#92400e",
      whiteSpace: "nowrap", flexShrink: 0,
      display: "inline-flex", alignItems: "center",
    }}>
      {item.pending_drafts} PENDING
    </span>
  );
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={onKey}
      style={{
        width: "100%", textAlign: "left", padding: "12px 14px",
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
        marginBottom: 8, cursor: "pointer",
        display: "flex", gap: 12, alignItems: "center",
        boxSizing: "border-box",
      }}
    >
      <FaUserCircle size={28} style={{ color: "#94a3b8", flexShrink: 0 }} />

      {/* Email + snippet stacked — flex:1 so the right rail can pin its
          chips/toggle/time as a single vertically-centered group. */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{
          fontSize: 14, fontWeight: 600, color: "#111827",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {item.contact_email || item.contact_key}
        </span>
        <span style={{
          fontSize: 13, color: "#6b7280",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {item.last_snippet || "—"}
        </span>
      </div>

      {/* Right rail — all aligned to the vertical center of the row so the
          score badge, PENDING pill, AI/Me toggle and timestamp share a
          single baseline instead of stacking next to the title only. */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
        flexWrap: isMobile ? "wrap" : "nowrap",
        justifyContent: "flex-end",
        ...(isMobile ? { maxWidth: "60%" } : {}),
      }}>
        <LeadScoreBadge
          score={item.lead_score}
          components={item.lead_components}
          lastEventAt={item.lead_last_event_at}
        />
        {pending}
        {ownerToggle}
        {time}
      </div>
    </div>
  );
});

// One bubble in the thread view. Channel icon + direction colour.
function ThreadMessage({ msg }) {
  const isOutbound = msg.direction === "outbound";
  const isSms = msg.channel === "sms";
  const isCall = msg.channel === "call";
  const bg = isCall
    ? "#fffbeb"
    : isSms
      ? (isOutbound ? "#dcfce7" : "#f0fdf4")
      : (isOutbound ? "#eef2ff" : "#f3f4f6");
  const border = isCall
    ? "#fde68a"
    : isSms
      ? (isOutbound ? "#86efac" : "#bbf7d0")
      : (isOutbound ? "#c7d2fe" : "#e5e7eb");
  const channelIcon = isCall
    ? <FaPhone size={9} />
    : isSms
      ? <FaCommentDots size={9} />
      : <FaEnvelope size={9} />;
  const headerLabel = isCall
    ? "Call note"
    : (isOutbound ? `AI → ${msg.to_email || ""}` : `${msg.from_email || ""} → AI`);
  return (
    <div style={{ display: "flex", justifyContent: isCall ? "center" : (isOutbound ? "flex-end" : "flex-start"), marginBottom: 10 }}>
      <div style={{
        maxWidth: isCall ? "92%" : "78%",
        padding: "10px 14px", borderRadius: 12,
        background: bg,
        border: `1px solid ${border}`,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 600, color: "#6b7280",
          marginBottom: 4, display: "flex", alignItems: "center", gap: 6,
        }}>
          {channelIcon}
          {headerLabel}
          <span style={{ marginLeft: "auto", fontWeight: 400 }}>{fmtRelative(msg.created_at)}</span>
        </div>
        {msg.subject && !isSms && !isCall && (
          <div style={{ fontSize: 12, color: "#111827", fontWeight: 600, marginBottom: 4 }}>
            {msg.subject}
          </div>
        )}
        <div style={{ fontSize: 13, color: "#1f2937", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
          {msg.message_text}
        </div>
      </div>
    </div>
  );
}

export function ThreadView({ contactKey, onBack, setToast, onActioned, onOwnershipChange, externalTags, onTagsChange, hideBack = false, hideNotes = false }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  // Start in the loading state so consumers (like AIConversationModal) that
  // mount ThreadView on click render the skeleton on the first paint instead
  // of flashing an empty header while the initial useEffect kicks in.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Local optimistic ownership flag — kept in sync with whatever the
  // /ai-conversations/thread endpoint returns. Defaults to true (the
  // server-side default for contacts with no contact_ai_settings row).
  const [aiOwns, setAiOwns] = useState(true);
  useEffect(() => {
    if (data && typeof data.ai_owns_relationship === "boolean") {
      setAiOwns(data.ai_owns_relationship);
    }
  }, [data]);

  // AI Handover Notes popover for the current contact (and any pinned
  // DraftCard rendered inside this view).
  const {
    popoverProps: notesPopoverProps,
    openHover: openNotesHover,
    openPinned: pinNotesPopover,
    closeHover: closeNotesHover,
  } = useHandoverNotesPopover({ isMobile });
  const notesPopover = useMemo(
    () => ({ openHover: openNotesHover, openPinned: pinNotesPopover, closeHover: closeNotesHover }),
    [openNotesHover, pinNotesPopover, closeNotesHover],
  );
  const notesContact = useMemo(() => ({
    first_name: data?.contact?.first_name,
    last_name: data?.contact?.last_name,
    email: data?.contact?.email,
    clean_mobile: data?.contact?.clean_mobile || data?.contact?.mobile,
  }), [data?.contact?.first_name, data?.contact?.last_name, data?.contact?.email, data?.contact?.clean_mobile, data?.contact?.mobile]);
  const notesCount = data?.contact?.handover_notes_count || 0;

  // Tags applied to this contact — mirror what /contacts renders so the
  // agent can add/remove tags directly from the AI Conversation modal.
  // When the modal is opened from /contacts, the parent passes in
  // `externalTags` (the row's current tag list) and `onTagsChange` so
  // edits made in the modal flow back into the contacts list and vice
  // versa. When no externalTags is provided (standalone /ai-conversations
  // route) we fall back to the thread response.
  const [tags, setTags] = useState(externalTags || []);
  useEffect(() => {
    if (externalTags) {
      setTags(externalTags);
    } else if (Array.isArray(data?.contact?.tags)) {
      setTags(data.contact.tags);
    }
  }, [externalTags, data?.contact?.tags]);
  const appliedSlugs = useMemo(() => new Set(tags.map((t) => t.slug)), [tags]);
  const tagBtnRef = useRef(null);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const tagIdentity = useMemo(() => (
    data?.contact ? getContactIdentity(data.contact) : null
  ), [data?.contact]);
  const addTag = useCallback(async (tagDef) => {
    if (!tagIdentity || (!tagIdentity.contact_email && !tagIdentity.contact_mobile)) return;
    // Optimistic insert (skip if already present).
    let next = null;
    setTags((cur) => {
      if (cur.some((t) => t.slug === tagDef.slug)) { next = cur; return cur; }
      next = [...cur, {
        slug: tagDef.slug, label: tagDef.label, color: tagDef.color,
        category: tagDef.category, source: "human",
      }];
      return next;
    });
    if (next) onTagsChange?.(contactKey, next);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/contact-tags/add`,
        { contacts: [tagIdentity], tag_slugs: [tagDef.slug] },
        { "Content-Type": "application/json" },
      );
    } catch (e) {
      // Roll back on failure.
      let rolled = null;
      setTags((cur) => { rolled = cur.filter((t) => t.slug !== tagDef.slug); return rolled; });
      if (rolled) onTagsChange?.(contactKey, rolled);
      setToast?.({ severity: "error", message: e?.response?.data?.message || "Failed to add tag." });
    }
  }, [tagIdentity, contactKey, onTagsChange, setToast]);
  const removeTag = useCallback(async (tagDef) => {
    if (!tagIdentity || (!tagIdentity.contact_email && !tagIdentity.contact_mobile)) return;
    const prev = tags;
    const next = tags.filter((t) => t.slug !== tagDef.slug);
    setTags(next);
    onTagsChange?.(contactKey, next);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/contact-tags/remove`,
        { contacts: [tagIdentity], tag_slug: tagDef.slug },
        { "Content-Type": "application/json" },
      );
    } catch (e) {
      setTags(prev);
      onTagsChange?.(contactKey, prev);
      setToast?.({ severity: "error", message: e?.response?.data?.message || "Failed to remove tag." });
    }
  }, [tagIdentity, tags, contactKey, onTagsChange, setToast]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    // Demo mode: serve the contact's sample thread (messages + any pending
    // draft) instead of hitting the blocked backend, so opening the AI
    // Conversation button from a /contacts sample row shows a real-looking
    // conversation. Falls back to an empty thread for contacts without a
    // seeded sample.
    if (typeof window !== "undefined" && window.__demoMode) {
      const demoThread = getDemoThread(contactKey);
      setData(demoThread || {
        status: "success", contact: {}, messages: [], pending_drafts: [],
      });
      setLoading(false);
      return;
    }
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/ai-conversations/thread`,
        { contact_key: contactKey },
        { "Content-Type": "application/json" }
      );
      if (res.data && res.data.status === "success") {
        setData(res.data);
      } else {
        setError(res.data?.message || "Failed to load thread");
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load thread");
    } finally {
      setLoading(false);
    }
  }, [contactKey]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => {
    load();
    onActioned?.();
  };

  // "Generate Follow Up" — calls the on-demand backend endpoint that
  // composes a new AI draft for this contact, dismisses any existing
  // pending draft, and inserts a fresh row into ai_agent_outbound_queue.
  // After it returns we reload the thread so the new draft surfaces in
  // pending_drafts (which the parent's onActioned also wants to know).
  //
  // The compose step takes ~20-30s, so we drive a fake progress bar that
  // creeps to ~95% over ~30s — same UX pattern as BroadcastNewsletterModal
  // and AIVideo so the agent doesn't think the button is dead.
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  // While streaming, a synthetic placeholder draft is rendered inside
  // pending_drafts so the textareas fill in live with the LLM's output.
  // It's swapped out for the real hydrated row on `done` (via load()).
  const [streamingDraft, setStreamingDraft] = useState(null);
  const updateStreamingDraft = useCallback((patch) => {
    setStreamingDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);
  // Optional free-text steer the agent can type before hitting Generate
  // Follow Up (e.g. "mention our open home on Saturday"). Forwarded as
  // `extra_instructions` to /ai-conversations/generate-followup and
  // injected into the compose prompt by the backend. Lives in a popover
  // anchored to the Generate Follow Up button so it stays out of the way
  // until the agent actually wants to use it.
  const [extraInstructions, setExtraInstructions] = useState("");
  const [showExtraPopover, setShowExtraPopover] = useState(false);
  const genTimer = useRef(null);
  const stopGenProgress = useCallback((complete) => {
    if (genTimer.current) {
      clearInterval(genTimer.current);
      genTimer.current = null;
    }
    if (complete) {
      setGenProgress(100);
      setTimeout(() => setGenProgress(0), 400);
    } else {
      setGenProgress(0);
    }
  }, []);
  useEffect(() => () => {
    if (genTimer.current) clearInterval(genTimer.current);
  }, []);
  const generateFollowUp = async () => {
    if (generating) return;
    // Demo mode: composing a fresh draft needs the backend, so don't fake it —
    // tell the agent this is sample data and leave the seeded draft in place.
    if (typeof window !== "undefined" && window.__demoMode) {
      setToast?.({ severity: "info", message: "This is sample data — generating a follow-up is disabled in the demo." });
      return;
    }
    setGenerating(true);
    // Kick off the fake progress curve.
    setGenProgress(0);
    const startTs = Date.now();
    const DURATION_MS = 30000;
    if (genTimer.current) clearInterval(genTimer.current);
    genTimer.current = setInterval(() => {
      const ratio = Math.min((Date.now() - startTs) / DURATION_MS, 1);
      setGenProgress(Math.min(95, ratio * 100 * (2 - ratio)));
    }, 200);

    // Seed a synthetic streaming draft so DraftCard mounts with empty
    // textareas the LLM tokens can write into in real time. Inherits
    // identity from the contact so the channel chips/header look right.
    const existing = Array.isArray(data?.pending_drafts) && data.pending_drafts.length > 0
      ? data.pending_drafts[0]
      : null;
    setStreamingDraft({
      id: "__streaming__",
      contact_key: contactKey,
      contact_email: existing?.contact_email
        || data?.contact?.email
        || (contactKey.includes("@") ? contactKey : null),
      contact_phone: existing?.contact_phone
        || data?.contact?.clean_mobile
        || data?.contact?.mobile
        || null,
      ai_agent_email: existing?.ai_agent_email || data?.ai_agent_email,
      ai_agent_name: existing?.ai_agent_name || data?.ai_agent_name,
      kind: "outbound",
      channel: existing?.channel || "email",
      subject: "",
      email_subject: "",
      body: "",
      email_body: "",
      sms_body: "",
      talking_points: "",
      call_note: "",
      created_at: new Date().toISOString(),
      metadata: { manual_trigger: true },
      trigger_names: ["manual_generate_followup"],
    });

    let streamErr = null;
    await streamGenerateFollowup({
      contactKey,
      extraInstructions,
      onPatch: (patch) => {
        setStreamingDraft((prev) => prev ? { ...prev, ...patch } : prev);
      },
      onDone: () => {},
      onError: (e) => { streamErr = e; },
    });

    if (streamErr) {
      setToast?.({
        severity: "error",
        message: streamErr.message || "Failed to generate follow-up.",
      });
      stopGenProgress(false);
      setStreamingDraft(null);
      setGenerating(false);
      return;
    }

    // Stream finished — stop the progress indicator immediately so the
    // bar doesn't keep creeping while we refetch the thread in the
    // background. The streaming placeholder stays visible (driven by
    // `generating`) until load() swaps in the hydrated row so the
    // textareas don't briefly flash back to the previous draft's text.
    stopGenProgress(true);
    setToast?.({ severity: "success", message: "Follow-up draft generated." });
    setExtraInstructions("");
    // Refetch the thread so the real hydrated draft replaces the
    // streaming placeholder with the proper queue id (needed for
    // Send / Dismiss to target the right row).
    await load();
    onActioned?.();
    setGenerating(false);
    setStreamingDraft(null);
  };

  const ownerToggle = (
    <AIOwnerToggle
      aiOwns={aiOwns}
      size={isMobile ? "sm" : "md"}
      onChange={(next) => postOwnership({
        contactKey,
        contactEmail: data?.contact?.email,
        next,
        applyLocal: (v) => { setAiOwns(v); onOwnershipChange?.(contactKey, v); },
        current: aiOwns,
        setToast,
      })}
    />
  );
  const notesBtn = (
    <HandoverNotesButton
      contact={notesContact}
      notesCount={notesCount}
      openHover={openNotesHover}
      openPinned={pinNotesPopover}
      closeHover={closeNotesHover}
      size={isMobile ? "sm" : "md"}
    />
  );
  const backBtn = (
    <button
      onClick={onBack}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px", borderRadius: 8, border: "1px solid #d1d5db",
        background: "#fff", color: "#374151", fontWeight: 600, fontSize: 12, cursor: "pointer",
        flexShrink: 0,
      }}
    >
      <FaArrowLeft size={11} /> Back
    </button>
  );
  const refreshBtn = (
    <button
      onClick={refresh}
      disabled={loading}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px", borderRadius: 8, border: "1px solid #d1d5db",
        background: "#fff", color: "#374151", fontWeight: 600, fontSize: 12,
        cursor: loading ? "default" : "pointer", flexShrink: 0,
      }}
    >
      <FaSyncAlt size={11} /> Refresh
    </button>
  );
  const hasPendingDraft = Array.isArray(data?.pending_drafts) && data.pending_drafts.length > 0;
  const generateBtn = (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <button
        onClick={() => {
          if (generating || loading) return;
          // Toggle the small instructions popover. The actual generate
          // call is fired from the popover's "Generate" submit button so
          // the agent always gets a chance to add a steer first.
          setShowExtraPopover(v => !v);
        }}
        disabled={generating || loading}
        title={hasPendingDraft
          ? "Replace the existing draft with a fresh AI follow-up"
          : "Have the AI draft a fresh follow-up for this contact"}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "6px 12px", borderRadius: 8,
          // Keep the border/background contrast strong while generating so
          // the white label stays readable — the previous "#99f6e4" pale
          // teal made the text wash out. Darken the active state instead.
          border: `1px solid ${generating ? "#0f766e" : "#0d9488"}`,
          background: generating ? "#0f766e" : "#0d9488",
          color: "#fff", fontWeight: 600, fontSize: 12,
          cursor: (generating || loading) ? "default" : "pointer",
          // Don't fade the whole button — we already changed the background
          // colour to signal the active state, and lowering opacity to 0.85
          // is what made the text hard to read.
          opacity: 1,
          transition: "background 0.12s ease, border-color 0.12s ease",
        }}
      >
        {generating ? (
          <span
            aria-hidden
            style={{
              width: 11, height: 11, borderRadius: "50%",
              border: "2px solid rgba(255,255,255,0.4)",
              borderTopColor: "#fff",
              display: "inline-block",
              animation: "ai-followup-spin 0.7s linear infinite",
            }}
          />
        ) : (
          <FaMagic size={11} />
        )}
        {generating ? "Generating…" : (hasPendingDraft ? "Regenerate" : "Generate Follow Up")}
      </button>

      {/* Hidden-by-default popover that holds the optional extra
          instructions textarea. Anchored to the button so it's clearly
          tied to the Generate Follow Up action. Click-outside and Escape
          dismiss it; submitting fires generateFollowUp(). */}
      {showExtraPopover && !generating && (
        <>
          <div
            onClick={() => setShowExtraPopover(false)}
            style={{
              position: "fixed", inset: 0, zIndex: 40, background: "transparent",
            }}
          />
          <div
            style={{
              position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 41,
              width: 320, maxWidth: "calc(100vw - 32px)",
              background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
              boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
              padding: 12,
            }}
            onKeyDown={(e) => { if (e.key === "Escape") setShowExtraPopover(false); }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
              Extra instructions (optional)
            </div>
            <textarea
              value={extraInstructions}
              onChange={(e) => setExtraInstructions(e.target.value)}
              rows={3}
              maxLength={2000}
              autoFocus
              placeholder="e.g. mention our open home on Saturday"
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "8px 10px", borderRadius: 8,
                border: "1px solid #d1d5db", fontSize: 13,
                fontFamily: "inherit", color: "#111827", background: "#fff",
                resize: "vertical", outline: "none",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
              <button
                onClick={() => setShowExtraPopover(false)}
                style={{
                  padding: "6px 10px", borderRadius: 8,
                  border: "1px solid #d1d5db", background: "#fff",
                  color: "#374151", fontWeight: 600, fontSize: 12, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowExtraPopover(false);
                  generateFollowUp();
                }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 10px", borderRadius: 8,
                  border: "1px solid #0d9488", background: "#0d9488",
                  color: "#fff", fontWeight: 600, fontSize: 12, cursor: "pointer",
                }}
              >
                <FaMagic size={11} /> Generate
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
  const contactInfo = (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {data?.contact?.first_name || data?.contact?.last_name
          ? `${data.contact.first_name || ""} ${data.contact.last_name || ""}`.trim()
          : (data?.contact?.email || contactKey)}
      </div>
      <div style={{ fontSize: 12, color: "#6b7280", display: "flex", gap: 10, flexWrap: "wrap" }}>
        {data?.contact?.email && <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>📧 {data.contact.email}</span>}
        {(data?.contact?.clean_mobile || data?.contact?.mobile) && <span>📱 {data.contact.clean_mobile || data.contact.mobile}</span>}
        {data?.contact?.suburb && <span>{data.contact.suburb} {data.contact.postcode}</span>}
        {data?.contact?.channels?.email?.available && !data.contact.channels.email.reachable && (
          <span style={{ color: "#dc2626" }}>📧 unsubscribed</span>
        )}
      </div>
      {/* Tag strip + "+ Tag" picker trigger — mirrors the per-row UI on
          /contacts so the agent can tag a contact without leaving the
          AI Conversation modal. zIndex bumped above the modal overlay. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6, alignItems: "center" }}>
        {tags.map((t) => (
          <ContactTagChip
            key={t.slug}
            tag={t}
            onRemove={tagIdentity ? () => removeTag(t) : undefined}
          />
        ))}
        <button
          ref={tagBtnRef}
          onClick={(e) => { e.stopPropagation(); if (!tagIdentity) return; setTagPickerOpen((v) => !v); }}
          disabled={!tagIdentity}
          title="Add a tag"
          style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            padding: "2px 6px", borderRadius: 999,
            background: "transparent",
            border: "1px dashed #d1d5db",
            color: "#6b7280", fontSize: 10, fontWeight: 600,
            cursor: tagIdentity ? "pointer" : "not-allowed",
          }}
        >
          <FaPlus style={{ fontSize: 8 }} /> Tag
        </button>
      </div>
      {tagPickerOpen && (
        <ContactTagPicker
          anchorEl={tagBtnRef.current}
          appliedSlugs={appliedSlugs}
          zIndex={10100}
          onClose={() => setTagPickerOpen(false)}
          onPick={(tagDef) => { addTag(tagDef); }}
          onRemove={(tagDef) => { removeTag(tagDef); }}
        />
      )}
    </div>
  );
  return (
    <div>
      {isMobile ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            {!hideBack && backBtn}
            <div style={{ flex: 1 }} />
            {generateBtn}
            {refreshBtn}
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            {contactInfo}
            <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
              {ownerToggle}
              {!hideNotes && notesBtn}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          {!hideBack && backBtn}
          {contactInfo}
          {!hideNotes && notesBtn}
          {ownerToggle}
          {generateBtn}
          {refreshBtn}
        </div>
      )}

      {error && (
        <div style={{ padding: "10px 14px", marginBottom: 16, borderRadius: 8, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Generate Follow Up progress — same pattern as
          BroadcastNewsletterModal so the agent gets visible feedback during
          the ~20-30s compose call instead of just a button spinner. The
          skeleton draft card itself is rendered in the Pending drafts
          section below so it visually replaces any existing draft while a
          regeneration is in flight. */}
      {generating && (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              width: "100%", height: 4, background: "#e5e7eb",
              borderRadius: 2, overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${genProgress}%`,
                background: "linear-gradient(90deg, #0d9488, #14b8a6)",
                borderRadius: 2,
                transition: genProgress === 100
                  ? "width 0.3s ease"
                  : "width 0.4s linear",
              }}
            />
          </div>
          <div
            style={{
              fontSize: 11, color: "#6b7280", marginTop: 6, textAlign: "center",
            }}
          >
            {genProgress < 95
              ? "Generating a follow-up — usually takes about 20–30 seconds…"
              : "Almost there…"}
          </div>
        </div>
      )}

      {loading && !data && (
        <div style={{ padding: 16, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10 }}>
          <ThreadMessageSkeleton outbound={false} />
          <ThreadMessageSkeleton outbound />
          <ThreadMessageSkeleton outbound={false} />
          <ThreadMessageSkeleton outbound />
          {SKELETON_KEYFRAMES}
        </div>
      )}

      {data && (
        <>
          {/* Pinned pending drafts.
              While a generation is in flight (`generating`), swap the
              existing draft cards out for a synthetic streaming card so
              the LLM's output fills the editable inputs live, in the
              same slot. Send / Dismiss are disabled until the real
              hydrated row replaces it once streaming completes. */}
          {(generating || (Array.isArray(data.pending_drafts) && data.pending_drafts.length > 0)) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                Pending drafts
              </div>
              {generating ? (
                streamingDraft ? (
                  <DraftCard
                    key="__streaming__"
                    item={streamingDraft}
                    onUpdateDraft={(_id, patch) => updateStreamingDraft(patch)}
                    setToast={setToast}
                    hideOwnership
                    hideRegenerate
                    isStreaming
                    notesPopover={notesPopover}
                  />
                ) : (
                  <>
                    <DraftCardSkeleton />
                    {SKELETON_KEYFRAMES}
                  </>
                )
              ) : (
                data.pending_drafts.map(d => (
                  <DraftCard
                    key={d.id}
                    item={d}
                    demo={typeof window !== "undefined" && !!window.__demoMode}
                    onActioned={refresh}
                    // Optimistically splice the dismissed/sent draft out of
                    // pending_drafts so it disappears from the modal
                    // instantly, then fire onActioned so the parent
                    // (and Needs Review list) refreshes in the background.
                    onRemove={(id) => {
                      setData(prev => prev ? {
                        ...prev,
                        pending_drafts: (prev.pending_drafts || []).filter(x => x.id !== id),
                      } : prev);
                      onActioned?.();
                    }}
                    setToast={setToast}
                    hideOwnership
                    hideRegenerate
                    onOwnershipChange={(ck, v) => {
                      setAiOwns(v);
                      onOwnershipChange?.(ck, v);
                    }}
                    notesPopover={notesPopover}
                  />
                ))
              )}
            </div>
          )}

          {/* Message log */}
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
            Conversation history
          </div>
          {(!data.messages || data.messages.length === 0) ? (
            <div style={{ padding: 32, textAlign: "center", color: "#9ca3af", fontSize: 13, border: "1px dashed #e5e7eb", borderRadius: 10, background: "#fff" }}>
              No messages yet.
            </div>
          ) : (
            <div style={{ padding: 16, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10 }}>
              {data.messages.map(m => (
                <ThreadMessage key={m.message_id || `${m.created_at}-${m.from_email}`} msg={m} />
              ))}
            </div>
          )}
        </>
      )}
      <AIHandoverNotesPopover {...notesPopoverProps} />
    </div>
  );
}

// Modal overlay that wraps ThreadView so the Needs Review tab can show
// an AI Conversation history modal — same pattern as the modal mounted
// from /contacts (components/AIConversationModal.js), inlined here to
// avoid a circular import (that file imports ThreadView from this one).
function ConversationHistoryModal({ contactKey, onClose, onOwnershipChange, isMobile, onActioned }) {
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!contactKey) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [contactKey]);

  useEffect(() => {
    if (!contactKey) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [contactKey, onClose]);

  if (!contactKey) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        // Offset by the desktop sidebar width (260px, see MainNav.js) so
        // the modal centres within the content pane on desktop.
        left: isMobile ? 0 : 260,
        background: "rgba(0,0,0,0.4)", backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: isMobile ? "stretch" : "center",
        padding: isMobile ? 0 : 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: isMobile ? 0 : 16,
          width: "100%",
          maxWidth: isMobile ? "none" : 880,
          height: isMobile ? "100%" : "auto",
          maxHeight: isMobile ? "100%" : "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          overflow: "hidden",
        }}
      >
        <div style={{
          display: "flex", justifyContent: "flex-end",
          padding: "12px 16px 0", flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            title="Close"
            style={{
              background: "none", border: "none", cursor: "pointer", padding: 6,
              color: "#9ca3af", fontSize: 16,
            }}
          >
            <FaTimes />
          </button>
        </div>
        <div style={{
          flex: 1, minHeight: 0, overflowY: "auto",
          padding: isMobile ? "8px 16px 24px" : "8px 24px 24px",
        }}>
          <ThreadView
            contactKey={contactKey}
            onBack={onClose}
            setToast={setToast}
            onActioned={onActioned || (() => {})}
            onOwnershipChange={onOwnershipChange}
            hideBack
            hideNotes
          />
        </div>
        <Toast toast={toast} onClose={() => setToast(null)} />
      </div>
    </div>
  );
}

export default function AIConversations({ mode = "review" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const tab = mode === "inbox" ? "all" : "review";
  // `modalContactKey` (declared further down) is the single source of truth
  // for which thread is open. Both the row-click "View conversation" button
  // and the deep-link `?contact=` param funnel through it so /ai-inbox and
  // /needs-review behave like /contacts — a modal overlay on top of the
  // list rather than a full-page take-over.

  // AI Handover Notes popover (shared with /contacts). Mounted once at the
  // page root so DraftCard rows on the Needs Review tab can all share it.
  const {
    popoverProps: notesPopoverProps,
    openHover: openNotesHover,
    openPinned: pinNotesPopover,
    closeHover: closeNotesHover,
  } = useHandoverNotesPopover({ isMobile });
  const notesPopover = useMemo(
    () => ({ openHover: openNotesHover, openPinned: pinNotesPopover, closeHover: closeNotesHover }),
    [openNotesHover, pinNotesPopover, closeNotesHover],
  );

  const [pendingItems, setPendingItems] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState("");
  // Needs Review tab UI state
  const [kindFilter, setKindFilter] = useState("all");        // "all" | "outbound" | "reply"
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkDismissing, setBulkDismissing] = useState(false);
  const [showBulkBuyCredit, setShowBulkBuyCredit] = useState(false);
  // Shared platform credit balance (same source as the /billing page) — used
  // to gate SMS sends client-side before they hit the backend, mirroring the
  // pattern in pages/PropertySearchDetail.js. Refetched after every send so
  // the next click sees the up-to-date number once the debit lands.
  const [balance, setBalance] = useState(null);
  const fetchBalance = useCallback(() => {
    handleTokenAndPostRequest(`${window.api_host}/billing/balance`)
      .then((res) => setBalance(res.data || null))
      .catch(() => setBalance(null));
  }, []);
  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  const [convoItems, setConvoItems] = useState([]);
  const [convoLoading, setConvoLoading] = useState(false);
  const [convoError, setConvoError] = useState("");
  const [convoSearch, setConvoSearch] = useState("");

  const [toast, setToast] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Demo/empty state: when the real queue (review) or conversation list
  // (inbox) comes back empty, we seed the corresponding list with sample data
  // so the agent sees what the page looks like in use. These flags drive the
  // demo banner, the network guard, and the refetch short-circuit below.
  const [demoReview, setDemoReview] = useState(false);
  const [demoInbox, setDemoInbox] = useState(false);

  // Only seed demo data once the backend has confirmed the agent has no rows
  // in production.contacts_merged. Read through a ref inside refreshPending /
  // refreshConvos so those callbacks see the latest value without needing to
  // change identity (which would re-trigger their effects unnecessarily).
  const agentHasNoContacts = useAgentHasNoContacts();
  const agentHasNoContactsRef = useRef(agentHasNoContacts);
  useEffect(() => { agentHasNoContactsRef.current = agentHasNoContacts; }, [agentHasNoContacts]);

  const refreshPending = useCallback(async () => {
    // While a demo is on screen the seeded sample data is the source of truth;
    // never refetch (which would return empty and wipe it).
    if (typeof window !== 'undefined' && window.__demoMode) return;
    setPendingLoading(true);
    setPendingError("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/ai-outbound-queue/list`,
        { status: "pending" },
        { "Content-Type": "application/json" }
      );
      if (res.data && res.data.status === "success") {
        const items = Array.isArray(res.data.items) ? res.data.items : [];
        // No real drafts → show the sample queue instead of "Inbox zero".
        if (items.length === 0) {
          if (agentHasNoContactsRef.current === true) {
            setDemoReview(true);
            setPendingItems(DEMO_REVIEW);
          } else {
            setDemoReview(false);
            setPendingItems([]);
          }
          setPendingLoading(false);
          return;
        }
        setDemoReview(false);
        setPendingItems(items);
        // Drop any selections that are no longer in the queue
        setSelectedIds(prev => {
          if (prev.size === 0) return prev;
          const valid = new Set(items.map(it => it.id));
          const next = new Set();
          prev.forEach(id => { if (valid.has(id)) next.add(id); });
          return next.size === prev.size ? prev : next;
        });
      } else {
        setPendingError(res.data?.message || "Failed to load queue");
      }
    } catch (e) {
      setPendingError(e?.response?.data?.message || "Failed to load queue");
    } finally {
      setPendingLoading(false);
    }
  }, []);

  // Splice an item out of the queue locally (no refetch). Called after a
  // successful send/dismiss instead of the old onActioned full-reload path.
  const handleRemove = useCallback((id) => {
    setPendingItems(prev => prev.filter(x => x.id !== id));
    setSelectedIds(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Mutate a queued draft's subject/body in place, so Send All sees the
  // edits the user has made on each card.
  const handleUpdateDraft = useCallback((id, patch) => {
    setPendingItems(prev => prev.map(x => x.id === id ? { ...x, ...patch } : x));
  }, []);

  // Swap a draft for a brand-new one (different id) in place. Called by
  // DraftCard after Regenerate succeeds so the new draft replaces the old
  // card without forcing a full /ai-outbound-queue/list refetch (which
  // would noticeably flicker the entire queue).
  const handleReplaceDraft = useCallback((oldId, newItem) => {
    if (!newItem || newItem.id == null) return;
    setPendingItems(prev => {
      const idx = prev.findIndex(x => x.id === oldId);
      if (idx === -1) return [newItem, ...prev];
      const next = prev.slice();
      next[idx] = newItem;
      return next;
    });
    setSelectedIds(prev => {
      if (!prev.has(oldId)) return prev;
      const next = new Set(prev);
      next.delete(oldId);
      next.add(newItem.id);
      return next;
    });
  }, []);

  const toggleSelected = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const refreshConvos = useCallback(async () => {
    if (typeof window !== 'undefined' && window.__demoMode) return;
    setConvoLoading(true);
    setConvoError("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/ai-conversations/list`,
        {},
        { "Content-Type": "application/json" }
      );
      if (res.data && res.data.status === "success") {
        const items = Array.isArray(res.data.items) ? res.data.items : [];
        // No real conversations → show the sample inbox.
        if (items.length === 0) {
          if (agentHasNoContactsRef.current === true) {
            setDemoInbox(true);
            setConvoItems(DEMO_INBOX);
          } else {
            setDemoInbox(false);
            setConvoItems([]);
          }
          setConvoLoading(false);
          return;
        }
        setDemoInbox(false);
        setConvoItems(items);
      } else {
        setConvoError(res.data?.message || "Failed to load conversations");
      }
    } catch (e) {
      setConvoError(e?.response?.data?.message || "Failed to load conversations");
    } finally {
      setConvoLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === "review") refreshPending(); }, [tab, refreshPending]);
  // Re-run the refresh once the has-contacts signal resolves so an
  // initially-empty queue gets the demo treatment (or doesn't) based on the
  // backend's verdict rather than the first-paint guess.
  useEffect(() => {
    if (agentHasNoContacts === undefined) return;
    if (tab === "review") refreshPending();
    if (tab === "all") refreshConvos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentHasNoContacts]);
  useEffect(() => { if (tab === "all") refreshConvos(); }, [tab, refreshConvos]);

  // Is the currently-visible tab showing seeded sample data? Drives the demo
  // banner and the network guard (which blocks every backend write/refetch).
  const showDemo = (tab === "review" && demoReview) || (tab === "all" && demoInbox);
  // Relax the guard while the upload or AI agent settings modal is open so
  // real uploads / settings fetches can reach the backend (otherwise the
  // settings modal would render with empty fields).
  useDemoNetworkGuard(showDemo && !showUploadModal && !showSettingsModal);

  // Backwards-compat: redirect legacy paths → /ai-inbox once.
  useEffect(() => {
    if (location.pathname === "/ai-outbound-messages" || location.pathname === "/ai-conversations") {
      navigate("/ai-inbox" + (location.search || ""), { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  // Deep-link: /ai-conversations?contact=<key> opens that contact's thread
  // directly. Used by the per-row "AI Conversation" button on /contacts.
  // We strip the param after consuming it so the back button leaves the
  // thread instead of re-opening it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const contactParam = params.get("contact");
    if (contactParam) {
      setModalContactKey(contactParam);
      params.delete("contact");
      const qs = params.toString();
      navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  const filteredConvos = useMemo(() => {
    const q = convoSearch.trim().toLowerCase();
    if (!q) return convoItems;
    return convoItems.filter(it =>
      (it.contact_email || "").toLowerCase().includes(q) ||
      (it.contact_key || "").toLowerCase().includes(q) ||
      (it.last_snippet || "").toLowerCase().includes(q)
    );
  }, [convoItems, convoSearch]);

  // Needs Review filter chips: counts and the visible (filtered) subset.
  const kindCounts = useMemo(() => {
    let outbound = 0, reply = 0;
    for (const it of pendingItems) {
      if ((it.kind || "outbound") === "reply") reply++; else outbound++;
    }
    return { all: pendingItems.length, outbound, reply };
  }, [pendingItems]);

  const filteredPending = useMemo(() => {
    if (kindFilter === "all") return pendingItems;
    return pendingItems.filter(it => (it.kind || "outbound") === kindFilter);
  }, [pendingItems, kindFilter]);

  const visibleIds = useMemo(() => filteredPending.map(it => it.id), [filteredPending]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some(id => selectedIds.has(id));

  const toggleAllVisible = useCallback(() => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (visibleIds.length > 0 && visibleIds.every(id => next.has(id))) {
        visibleIds.forEach(id => next.delete(id));
      } else {
        visibleIds.forEach(id => next.add(id));
      }
      return next;
    });
  }, [visibleIds]);

  // Sends every checked draft sequentially. Each successful send removes the
  // card locally; failures stay in the queue with their selection intact.
  const sendAll = useCallback(async () => {
    if (selectedIds.size === 0) {
      setToast({ severity: "error", message: "Tick at least one draft to send" });
      return;
    }
    // Demo: simulate the batch locally — drop the selected cards, no credit
    // gate, no network.
    if (showDemo) {
      Array.from(selectedIds).forEach(id => handleRemove(id));
      setToast({ severity: "success", message: "Sample data — nothing was actually sent." });
      return;
    }
    setBulkSending(true);
    const ids = Array.from(selectedIds);
    let ok = 0, fail = 0, creditFail = 0, callSkipped = 0;
    // Track running platform balance locally so consecutive SMS sends in the
    // same batch are gated correctly without needing /billing/balance to
    // refetch between each row.
    let runningBalance = Number(balance?.balance_amount ?? 0);
    let anySmsSent = false;
    for (const id of ids) {
      const it = pendingItems.find(x => x.id === id);
      if (!it) continue;
      // Call drafts can't be bulk-sent — they need a per-card outcome
      // entered by the agent, so skip them here.
      if (it.channel === "call") {
        callSkipped++;
        continue;
      }
      const isSms = (it.channel === "sms");
      const cleanSubject = ((it.email_subject ?? it.subject) || "").trim();
      const cleanEmailBody = ((it.email_body
          ?? (it.channel !== "sms" ? it.body : "")) || "").trim();
      const cleanSmsBody = ((it.sms_body
          ?? (it.channel === "sms" ? it.body : "")) || "").trim();
      if (isSms ? !cleanSmsBody : (!cleanEmailBody || !cleanSubject)) {
        fail++; continue;
      }
      if (isSms) {
        const cost = smsCost(cleanSmsBody);
        if (cost > runningBalance + 1e-9) {
          creditFail++;
          fail++;
          continue;
        }
        runningBalance -= cost;
      }
      const payload = isSms
        ? { channel: "sms", body: cleanSmsBody, contact_phone: it.contact_phone }
        : { channel: "email", subject: cleanSubject, body: cleanEmailBody };
      try {
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/ai-outbound-queue/${id}/send`,
          payload,
          { "Content-Type": "application/json" }
        );
        if (res.data && res.data.status === "success") {
          ok++;
          if (isSms) anySmsSent = true;
          handleRemove(id);
        } else {
          fail++;
        }
      } catch (e) {
        fail++;
      }
    }
    setBulkSending(false);
    if (anySmsSent) fetchBalance();
    if (creditFail > 0) {
      setShowBulkBuyCredit(true);
    }
    setToast({
      severity: fail === 0 ? "success" : "error",
      message: fail === 0
        ? `Sent ${ok}${callSkipped > 0 ? ` (${callSkipped} call${callSkipped === 1 ? "" : "s"} skipped — save individually)` : ""}`
        : (ok === 0
            ? (creditFail > 0
                ? `Out of SMS credit — ${creditFail} could not send`
                : `Failed to send ${fail}`)
            : `Sent ${ok}, ${fail} failed${creditFail > 0 ? ` (${creditFail} need credit)` : ""}${callSkipped > 0 ? ` (${callSkipped} call${callSkipped === 1 ? "" : "s"} skipped)` : ""}`),
    });
  }, [selectedIds, pendingItems, handleRemove, balance, fetchBalance, showDemo]);

  // Dismisses every checked draft sequentially. Same shape as sendAll —
  // successful dismisses splice the row locally; failures keep their slot.
  const dismissAll = useCallback(async () => {
    if (selectedIds.size === 0) {
      setToast({ severity: "error", message: "Tick at least one draft to dismiss" });
      return;
    }
    setBulkDismissing(true);
    const ids = Array.from(selectedIds);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/ai-outbound-queue/${id}/dismiss`,
          {},
          { "Content-Type": "application/json" }
        );
        if (res.data && res.data.status === "success") {
          ok++;
          handleRemove(id);
        } else {
          fail++;
        }
      } catch (e) {
        fail++;
      }
    }
    setBulkDismissing(false);
    setToast({
      severity: fail === 0 ? "success" : "error",
      message: fail === 0
        ? `Dismissed ${ok}`
        : (ok === 0 ? `Failed to dismiss ${fail}` : `Dismissed ${ok}, ${fail} failed`),
    });
  }, [selectedIds, handleRemove]);

  // Stable identities so the memoized DraftCard / ConversationRow rows
  // don't tear down on every parent render.
  const onOpenThread = useCallback((key) => setModalContactKey(key), []);

  // Contact key whose AI Conversation history is currently shown in the
  // modal overlay (Needs Review tab). Mirrors the /contacts pattern so
  // the user stays on the queue while reviewing past conversation.
  const [modalContactKey, setModalContactKey] = useState(null);
  const onOpenConversationModal = useCallback((key) => setModalContactKey(key), []);
  const onCloseConversationModal = useCallback(() => {
    setModalContactKey(null);
  }, []);

  // Per-contact Property Searches modal (Needs Review tab). Mirrors the
  // /contacts pattern: holds { query, label } and is shown via the
  // <ContactSearchesModal /> rendered at the bottom of the page.
  const [searchesModal, setSearchesModal] = useState(null);
  const onOpenSearches = useCallback((query, item) => {
    if (!query) return;
    const label = [item?.contact_first_name, item?.contact_last_name]
      .filter(Boolean).join(" ")
      || item?.contact_email
      || item?.contact_phone
      || query;
    setSearchesModal({ query, label });
  }, []);

  // Per-contact Activity timeline modal (Needs Review tab). Mirrors the
  // /contacts pattern: holds a contact-shaped object so the shared
  // <ContactActivityModal /> can scope its /activity-explorer query.
  const [activityContact, setActivityContact] = useState(null);
  const onOpenActivity = useCallback((item) => {
    if (!item?.contact_email) return;
    setActivityContact({
      email: item.contact_email,
      first_name: item.contact_first_name,
      last_name: item.contact_last_name,
      mobile: item.contact_phone,
      clean_mobile: item.contact_phone,
    });
  }, []);

  // When the AI/Me toggle flips on a card or row, mirror it across the
  // other lists so the user doesn't see the old value when switching tabs.
  // Keyed by contact_key so cards for the same contact stay in sync.
  const onOwnershipChange = useCallback((contactKey, aiOwns) => {
    if (!contactKey) return;
    setPendingItems(prev => prev.map(it =>
      (it.contact_key || (it.contact_email || "").toLowerCase()) === contactKey
        ? { ...it, ai_owns_relationship: aiOwns }
        : it
    ));
    setConvoItems(prev => prev.map(it =>
      it.contact_key === contactKey
        ? { ...it, ai_owns_relationship: aiOwns }
        : it
    ));
  }, []);

  const pendingCount = pendingItems.length;

  // Notify the sidebar so the "Needs Review" badge updates as the
  // queue drains/fills without waiting for the next 60s poll. Seeded demo
  // drafts are sample data, so report 0 to keep the real badge accurate.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('needs-review-count-changed', {
      detail: { count: demoReview ? 0 : pendingCount },
    }));
  }, [pendingCount, demoReview]);

  // (Removed) The page used to take over with a full-screen ThreadView when
  // activeContactKey was set; we now render ConversationHistoryModal at the
  // bottom of the tree so the list stays visible behind it.


  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", width: "100%" }}>
    <div style={{ padding: 24, maxWidth: 880, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8, gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ fontSize: isMobile ? 20 : 22, fontWeight: 700, color: "#111827", margin: 0, flex: isMobile ? "1 1 100%" : 1 }}>
          {tab === "review" ? (
            <>Needs Review{pendingCount > 0 && (
              <span style={{
                marginLeft: 10, fontSize: 12, padding: "2px 8px", borderRadius: 999,
                background: "#7c3aed", color: "#fff", fontWeight: 700, verticalAlign: "middle",
              }}>{pendingCount}</span>
            )}</>
          ) : "AI Inbox"}
        </h1>
        <button
          onClick={() => setShowSettingsModal(true)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 12px", borderRadius: 8, border: "1px solid #d1d5db",
            background: "#fff", color: "#374151", fontWeight: 600, fontSize: 12, cursor: "pointer",
            flexShrink: 0,
          }}
          title="Configure your AI agent"
        >
          <FaCog size={11} /> Configure
        </button>
        <button
          onClick={() => { if (tab === "review") refreshPending(); else refreshConvos(); }}
          disabled={tab === "review" ? pendingLoading : convoLoading}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 12px", borderRadius: 8, border: "1px solid #d1d5db",
            background: "#fff", color: "#374151", fontWeight: 600, fontSize: 12,
            cursor: "pointer", flexShrink: 0,
          }}
        >
          <FaSyncAlt size={11} /> Refresh
        </button>
      </div>

      <p style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.5, marginTop: 0, marginBottom: 18 }}>
        {tab === "review" ? (
          <>Drafts your AI agent has lined up — proactive outbound and replies to
          inbound emails. Approve, edit, or dismiss each one. Switch the modes
          on or off in <b>Settings → AI Agent</b>.</>
        ) : (
          <>All AI conversations with your contacts. Open any thread to see the
          full history or jump back into a reply.</>
        )}
      </p>


      {tab === "review" && (
        <>
          {showDemo && (
            <DemoBanner
              isMobile={isMobile}
              title="Here's how Needs Review looks with drafts"
              subtitle="Sample data — your AI agent queues replies and follow-ups here for you to approve or edit."
              ctaLabel={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><FaUpload size={13} /> Upload Contacts</span>}
              onCta={() => setShowUploadModal(true)}
            />
          )}
          {pendingError && (
            <div style={{ padding: "10px 14px", marginBottom: 16, borderRadius: 8, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", fontSize: 13 }}>
              {pendingError}
            </div>
          )}
          {pendingItems.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              {[
                { key: "all",      label: "All" },
                { key: "outbound", label: "Outbound" },
                { key: "reply",    label: "Reply" },
              ].map(f => {
                const active = kindFilter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setKindFilter(f.key)}
                    style={{
                      padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                      border: "1px solid",
                      borderColor: active ? "#7c3aed" : "#e5e7eb",
                      background: active ? "#7c3aed" : "#fff",
                      color: active ? "#fff" : "#374151",
                      cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                  >
                    {f.label}
                    <span style={{
                      fontSize: 11, padding: "1px 7px", borderRadius: 999, fontWeight: 700,
                      background: active ? "rgba(255,255,255,0.22)" : "#f3f4f6",
                      color: active ? "#fff" : "#6b7280",
                    }}>{kindCounts[f.key]}</span>
                  </button>
                );
              })}
              <label style={{
                marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 6,
                fontSize: 12, color: "#374151",
                cursor: visibleIds.length === 0 ? "default" : "pointer",
                opacity: visibleIds.length === 0 ? 0.5 : 1,
              }}>
                <input
                  type="checkbox"
                  ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  disabled={visibleIds.length === 0}
                  style={{ width: 16, height: 16, accentColor: "#7c3aed", cursor: visibleIds.length === 0 ? "default" : "pointer", margin: 0 }}
                />
                Select all
              </label>
              {!isMobile && (
                <div style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <button
                    onClick={dismissAll}
                    disabled={bulkSending || bulkDismissing || selectedIds.size === 0}
                    style={{
                      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                      padding: "6px 14px", borderRadius: 8,
                      border: "1px solid #d1d5db", background: "#fff",
                      color: "#374151", fontWeight: 600, fontSize: 13,
                      cursor: (bulkSending || bulkDismissing || selectedIds.size === 0) ? "default" : "pointer",
                      opacity: (bulkSending || bulkDismissing || selectedIds.size === 0) ? 0.6 : 1,
                    }}
                  >
                    <FaTimes size={11} />
                    {bulkDismissing ? "Dismissing…" : "Dismiss"}
                    {!bulkDismissing && selectedIds.size > 0 && (
                      <span style={{
                        fontSize: 11, padding: "1px 7px", borderRadius: 999,
                        background: "#e5e7eb", color: "#374151", fontWeight: 700,
                        minWidth: 16, textAlign: "center", lineHeight: 1.4,
                      }}>{selectedIds.size}</span>
                    )}
                  </button>
                  <button
                    onClick={sendAll}
                    disabled={bulkSending || bulkDismissing || selectedIds.size === 0}
                    style={{
                      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                      padding: "6px 14px", borderRadius: 8, border: "none",
                      background: (bulkSending || bulkDismissing || selectedIds.size === 0)
                        ? "#a78bfa" : "linear-gradient(90deg, #7c3aed, #5b21b6)",
                      color: "#fff", fontWeight: 600, fontSize: 13,
                      cursor: (bulkSending || bulkDismissing || selectedIds.size === 0) ? "default" : "pointer",
                    }}
                  >
                    <FaPaperPlane size={11} />
                    {bulkSending ? "Sending…" : "Send"}
                    {!bulkSending && selectedIds.size > 0 && (
                      <span style={{
                        fontSize: 11, padding: "1px 7px", borderRadius: 999,
                        background: "rgba(255,255,255,0.25)", color: "#fff", fontWeight: 700,
                        minWidth: 16, textAlign: "center", lineHeight: 1.4,
                      }}>{selectedIds.size}</span>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
          {pendingItems.length > 0 && isMobile && (
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <button
                onClick={dismissAll}
                disabled={bulkSending || bulkDismissing || selectedIds.size === 0}
                style={{
                  flex: 1,
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                  padding: "8px 14px", borderRadius: 8,
                  border: "1px solid #d1d5db", background: "#fff",
                  color: "#374151", fontWeight: 600, fontSize: 13,
                  cursor: (bulkSending || bulkDismissing || selectedIds.size === 0) ? "default" : "pointer",
                  opacity: (bulkSending || bulkDismissing || selectedIds.size === 0) ? 0.6 : 1,
                }}
              >
                <FaTimes size={11} />
                {bulkDismissing ? "Dismissing…" : "Dismiss"}
                {!bulkDismissing && selectedIds.size > 0 && (
                  <span style={{
                    fontSize: 11, padding: "1px 7px", borderRadius: 999,
                    background: "#e5e7eb", color: "#374151", fontWeight: 700,
                    minWidth: 16, textAlign: "center", lineHeight: 1.4,
                  }}>{selectedIds.size}</span>
                )}
              </button>
              <button
                onClick={sendAll}
                disabled={bulkSending || bulkDismissing || selectedIds.size === 0}
                style={{
                  flex: 1,
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                  padding: "8px 14px", borderRadius: 8, border: "none",
                  background: (bulkSending || bulkDismissing || selectedIds.size === 0)
                    ? "#a78bfa" : "linear-gradient(90deg, #7c3aed, #5b21b6)",
                  color: "#fff", fontWeight: 600, fontSize: 13,
                  cursor: (bulkSending || bulkDismissing || selectedIds.size === 0) ? "default" : "pointer",
                }}
              >
                <FaPaperPlane size={11} />
                {bulkSending ? "Sending…" : "Send"}
                {!bulkSending && selectedIds.size > 0 && (
                  <span style={{
                    fontSize: 11, padding: "1px 7px", borderRadius: 999,
                    background: "rgba(255,255,255,0.25)", color: "#fff", fontWeight: 700,
                    minWidth: 16, textAlign: "center", lineHeight: 1.4,
                  }}>{selectedIds.size}</span>
                )}
              </button>
            </div>
          )}
          {pendingLoading ? (
            <div>
              {[0, 1, 2].map(i => <DraftCardSkeleton key={i} />)}
              {SKELETON_KEYFRAMES}
            </div>
          ) : pendingItems.length === 0 ? (
            <div style={{ padding: 48, textAlign: "center", border: "1px dashed #cbd5e1", borderRadius: 12, background: "#fff", color: "#6b7280" }}>
              <FaInbox size={28} style={{ color: "#94a3b8", marginBottom: 10 }} />
              <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                Inbox zero
              </div>
              <div style={{ fontSize: 13 }}>
                No drafts waiting for review. New drafts appear here when the AI replies to a contact or queues a follow-up.
              </div>
            </div>
          ) : filteredPending.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", border: "1px dashed #cbd5e1", borderRadius: 12, background: "#fff", color: "#6b7280", fontSize: 13 }}>
              No {kindFilter} drafts. Switch the filter to see the rest.
            </div>
          ) : (
            filteredPending.map(item => (
              <DraftCard
                key={item.id}
                item={item}
                demo={showDemo}
                onRemove={handleRemove}
                onUpdateDraft={handleUpdateDraft}
                onReplace={handleReplaceDraft}
                onRegenerated={refreshPending}
                selected={selectedIds.has(item.id)}
                onToggleSelected={toggleSelected}
                setToast={setToast}
                onOpenThread={onOpenThread}
                onOpenConversationModal={onOpenConversationModal}
                onOpenSearches={onOpenSearches}
                onOpenActivity={onOpenActivity}
                onOwnershipChange={onOwnershipChange}
                balance={balance}
                onBalanceChange={fetchBalance}
                notesPopover={notesPopover}
              />
            ))
          )}
          {/* Spacer so the last draft clears the fixed demo banner. */}
          {showDemo && <div style={{ height: isMobile ? 180 : 100 }} />}
        </>
      )}

      {tab === "all" && (
        <>
          {showDemo && (
            <DemoBanner
              isMobile={isMobile}
              title="Here's how the AI Inbox looks with activity"
              subtitle="Sample data — every contact your AI agent emails or texts shows up here as a thread."
              ctaLabel={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><FaUpload size={13} /> Upload Contacts</span>}
              onCta={() => setShowUploadModal(true)}
            />
          )}
          <input
            type="text"
            placeholder="Search by name, email, or message…"
            value={convoSearch}
            onChange={(e) => setConvoSearch(e.target.value)}
            style={{
              width: "100%", padding: "8px 12px", borderRadius: 8,
              border: "1px solid #d1d5db", fontSize: 13, marginBottom: 12,
              boxSizing: "border-box",
            }}
          />
          {convoError && (
            <div style={{ padding: "10px 14px", marginBottom: 16, borderRadius: 8, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", fontSize: 13 }}>
              {convoError}
            </div>
          )}
          {convoLoading ? (
            <div>
              {[0, 1, 2, 3, 4, 5].map(i => <ConversationRowSkeleton key={i} />)}
              {SKELETON_KEYFRAMES}
            </div>
          ) : filteredConvos.length === 0 ? (
            <div style={{ padding: 48, textAlign: "center", border: "1px dashed #cbd5e1", borderRadius: 12, background: "#fff", color: "#6b7280" }}>
              <div style={{ fontSize: 13 }}>
                No conversations yet. They'll appear here once the AI emails a contact or replies to one.
              </div>
            </div>
          ) : (
            filteredConvos.map(it => (
              <ConversationRow
                key={it.contact_key}
                item={it}
                onOpen={showDemo
                  ? (() => setToast({ severity: "info", message: "Sample conversation — connect a contact to view the full thread." }))
                  : onOpenThread}
                setToast={setToast}
                onOwnershipChange={onOwnershipChange}
              />
            ))
          )}
          {/* Spacer so the last row clears the fixed demo banner. */}
          {showDemo && <div style={{ height: isMobile ? 180 : 100 }} />}
        </>
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />

      <AIAgentSettingsModal
        show={showSettingsModal}
        onHide={() => setShowSettingsModal(false)}
      />
      <UploadContactsModal show={showUploadModal} onClose={() => setShowUploadModal(false)} />
      {showBulkBuyCredit && (
        <BuyExtraUsageModal
          onClose={() => setShowBulkBuyCredit(false)}
          defaultAmount={25}
          reason="One or more selected drafts couldn't send because you're out of SMS credit. Top up to continue."
        />
      )}
      <ConversationHistoryModal
        contactKey={modalContactKey}
        onClose={onCloseConversationModal}
        onOwnershipChange={onOwnershipChange}
        isMobile={isMobile}
        onActioned={() => { refreshPending(); refreshConvos(); }}
      />
      <ContactSearchesModal
        searchQuery={searchesModal?.query || null}
        contactLabel={searchesModal?.label || ""}
        onClose={() => setSearchesModal(null)}
      />
      <ContactActivityModal
        contact={activityContact}
        onClose={() => setActivityContact(null)}
      />
      <AIHandoverNotesPopover {...notesPopoverProps} />
    </div>
    </div>
  );
}
